import { useState, useRef, useEffect, useCallback } from "react";
import {
  Shield, Smartphone, QrCode, LayoutDashboard, Ticket, Pencil, Bell,
  Bot, User, Headset, Paperclip, Eye, EyeOff, Plus, LogOut, X, Send,
  Building2, Lock, Settings, BarChart3, Wrench, Volume2, ClipboardList,
  Info, Siren, MessageSquare, HelpCircle, TrendingUp, CircleDot, CheckCircle2,
  Clock, MapPin, Sparkles, ShieldCheck, Film, Printer, Ruler, Mail, Trash2,
} from "lucide-react";

/* ─── LOGO ─── */
/* ─── LOGO SVG (fiel ao original) ─── */
/* Dois balões de fala sobrepostos + "ESCUTA" */
const LogoSVG = ({width=140, color="#EC4899", bgColor="transparent"}) => (
  <svg viewBox="0 0 390 102" xmlns="http://www.w3.org/2000/svg"
       style={{display:"block", width, height:"auto", flexShrink:0}}>
    {/* Balão traseiro (menor) */}
    <rect x="4" y="3" width="56" height="60" rx="12" fill="none" stroke={color} strokeWidth="6.5"/>
    {/* Balão frontal — fill cobre o traseiro */}
    <rect x="19" y="18" width="63" height="64" rx="12" fill={bgColor} strokeWidth="0"/>
    <rect x="19" y="18" width="63" height="64" rx="12" fill="none" stroke={color} strokeWidth="6.5"/>
    {/* Ponteiro do balão frontal */}
    <path d={`M25,80 L16,99 L46,82`} fill={color}/>
    {/* ESCUTA */}
    <text x="98" y="76"
          fontFamily="'Outfit','Arial Black',Impact,sans-serif"
          fontWeight="900" fontSize="68" fill={color} letterSpacing="-1">
      ESCUTA
    </text>
    {/* Subtítulo */}
    <text x="100" y="95"
          fontFamily="'Outfit',Arial,sans-serif"
          fontWeight="500" fontSize="10" fill={color} letterSpacing="1.8">
      TRANSFORMANDO FEEDBACKS EM RESULTADO
    </text>
  </svg>
);

/* Renderiza o logo como SVG string para uso no canvas */
function logoSvgString(color, bgColor, W, H) {
  const sc = 390/102; // aspect ratio
  return `<svg viewBox="0 0 390 102" xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">
    <rect x="4" y="3" width="56" height="60" rx="12" fill="none" stroke="${color}" stroke-width="6.5"/>
    <rect x="19" y="18" width="63" height="64" rx="12" fill="${bgColor}" stroke-width="0"/>
    <rect x="19" y="18" width="63" height="64" rx="12" fill="none" stroke="${color}" stroke-width="6.5"/>
    <path d="M25,80 L16,99 L46,82" fill="${color}"/>
    <text x="98" y="76" font-family="Arial Black,sans-serif" font-weight="900" font-size="68" fill="${color}" letter-spacing="-1">ESCUTA</text>
    <text x="100" y="95" font-family="Arial,sans-serif" font-weight="500" font-size="10" fill="${color}" letter-spacing="1.8">TRANSFORMANDO FEEDBACKS EM RESULTADO</text>
  </svg>`;
}

async function loadLogoOnCanvas(color, bgColor, targetW) {
  const H = Math.round(targetW * (102/390));
  const svg = logoSvgString(color, bgColor, targetW, H);
  const blob = new Blob([svg], {type:'image/svg+xml'});
  const url = URL.createObjectURL(blob);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve({img, w:targetW, h:H}); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/* ─── ESTILOS ─── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:'Plus Jakarta Sans',sans-serif; }
    ::-webkit-scrollbar { width:7px;height:7px; } ::-webkit-scrollbar-thumb { background:#D8DCE8; border-radius:8px; } ::-webkit-scrollbar-thumb:hover { background:#C2C8D8; }
    @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
    @keyframes shake   { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-5px)} 40%,80%{transform:translateX(5px)} }
    @keyframes dotP    { 0%,100%{opacity:1} 50%{opacity:.2} }
    @keyframes spin    { to{transform:rotate(360deg)} }
    @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    .fu  { animation:fadeUp  .3s ease both; }
    .fi  { animation:fadeIn  .22s ease both; }
    .shk { animation:shake   .4s ease; }
    .dot span { display:inline-block;width:6px;height:6px;border-radius:50%;background:#9CA3AF;margin:0 2px;animation:dotP 1.2s ease infinite; }
    .dot span:nth-child(2){animation-delay:.2s} .dot span:nth-child(3){animation-delay:.4s}
    .nav-item { display:flex;align-items:center;gap:10px;padding:9px 11px;border-radius:11px;font-size:13.5px;font-weight:600;color:#6B7280;cursor:pointer;transition:all .15s;user-select:none; }
    .nav-item:hover { background:#F1F2F8;color:#20264B; }
    .nav-item.on { background:rgba(236,72,153,.1);color:#EC4899; }
    .card { background:#FFFFFF;border:1px solid #ECEDF3;border-radius:16px;padding:18px;box-shadow:0 1px 3px rgba(31,37,64,.04); }
    .cc   { background:#FFFFFF;border:1px solid #ECEDF3;border-radius:16px;overflow:hidden;cursor:pointer;transition:all .2s;box-shadow:0 1px 3px rgba(31,37,64,.04); }
    .cc:hover { border-color:#EC4899;transform:translateY(-2px);box-shadow:0 12px 30px rgba(236,72,153,.14); }
    .inp  { background:#F6F7FB;border:1.5px solid #ECEDF3;border-radius:11px;padding:11px 14px;color:#20264B;font-size:14px;outline:none;width:100%;transition:all .2s;font-family:"Plus Jakarta Sans",sans-serif; }
    .inp:focus { border-color:#EC4899;background:#fff;box-shadow:0 0 0 4px rgba(236,72,153,.1); } .inp::placeholder { color:#9CA3AF; } .inp.err { border-color:#ef4444; }
    .tag  { display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600; }
    .mbtn { background:#EC4899;color:#fff;border:none;border-radius:999px;padding:11px 20px;font-size:14px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .2s;font-family:"Plus Jakarta Sans",sans-serif;box-shadow:0 8px 20px rgba(236,72,153,.28); }
    .mbtn:hover:not(:disabled) { background:#DB2777;transform:translateY(-1px);box-shadow:0 12px 26px rgba(236,72,153,.4); }
    .mbtn:disabled { opacity:.45;cursor:not-allowed;box-shadow:none; }
    .gbtn { background:#fff;color:#6B7280;border:1.5px solid #ECEDF3;border-radius:999px;padding:10px 17px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .2s;font-family:"Plus Jakarta Sans",sans-serif; }
    .gbtn:hover { background:#F1F2F8;color:#20264B;border-color:#8B5CF6; }
    .rbtn { background:#fff;color:#ef4444;border:1.5px solid rgba(239,68,68,.25);border-radius:999px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .2s;font-family:"Plus Jakarta Sans",sans-serif; }
    .rbtn:hover { background:rgba(239,68,68,.07);border-color:#ef4444; }
    .row  { display:flex;align-items:center;gap:10px;padding:11px 0;border-bottom:1px solid #EEF0F6;cursor:pointer;transition:opacity .15s; }
    .row:hover { opacity:.65; }
    .mbg  { position:fixed;inset:0;background:rgba(32,38,75,.42);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:200;animation:fadeIn .2s;padding:16px; }
    .modal { background:#FFFFFF;border:1px solid #ECEDF3;border-radius:20px;padding:26px;width:460px;max-width:94vw;animation:fadeUp .28s;max-height:90vh;overflow-y:auto;box-shadow:0 30px 70px rgba(31,37,64,.22); }
    .tab-btn { padding:9px 15px;background:none;border:none;border-bottom:2px solid transparent;font-size:14px;font-weight:600;color:#9CA3AF;cursor:pointer;transition:all .15s;font-family:"Plus Jakarta Sans",sans-serif;margin-bottom:-1px; }
    .tab-btn.on { color:#20264B;border-bottom-color:#EC4899;font-weight:700; }
    .spin-i { width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:spin .8s linear infinite;display:inline-block; }
    .toast-wrap { position:fixed;bottom:16px;right:16px;z-index:999;display:flex;flex-direction:column;gap:8px;max-width:320px;pointer-events:none; }
    .toast-item { padding:12px 16px;border-radius:12px;font-size:13px;font-weight:600;animation:slideUp .3s ease;box-shadow:0 10px 30px rgba(31,37,64,.12); }
    /* ── MOBILE ── */
    @media (max-width:768px) {
      .sidebar-desktop { display:none!important; }
      .mobile-nav      { display:flex!important; }
      .topbar-label    { display:none!important; }
      .main-padding    { padding:12px!important; }
      .ticket-panel    { flex-direction:column!important; height:auto!important; }
      .ticket-list-col { width:100%!important; max-height:260px; }
      .modal           { width:97vw!important; padding:16px!important; }
      .grid-2col       { grid-template-columns:1fr!important; }
      .stat-grid       { grid-template-columns:1fr 1fr!important; gap:8px!important; }
      .chat-box        { height:calc(100dvh - 140px)!important; }
      .qr-flex         { flex-direction:column!important; }
      .cc-grid         { grid-template-columns:1fr!important; }
    }
    .mobile-nav { display:none; }
  `}</style>
);

/* ─── CONSTANTES ─── */
const ACCENTS = ["#EC4899","#0891b2","#7c3aed","#db2777","#ea580c","#65a30d"];
const TYPES   = ["Residencial","Comercial","Shopping","Hospital","Escritório","Outro"];
const EMOJIS  = ["🏢","🏠","🏡","🏗","🌆","🏰","🏙","🏬","🌇","🏛","🌴","🌿"];
const CATS    = [
  {id:2, icon:"🔧", name:"Manutenção e Reparos", esc:true},
  {id:3, icon:"🔊", name:"Barulho e Conduta",     esc:true},
  {id:4, icon:"📋", name:"Dúvidas sobre Regras",  esc:false},
  {id:7, icon:"ℹ️",  name:"Informações Gerais",    esc:false},
  {id:8, icon:"🚨", name:"Emergências",            esc:true, urgent:true},
  {id:9, icon:"💬", name:"Sugestões e Elogios",    esc:false},
  {id:10,icon:"❓", name:"Outros",                 esc:false},
];
const SM = {
  open:          {label:"Aberto",     color:"#f59e0b", bg:"rgba(245,158,11,.14)"},
  "in-progress": {label:"Em atend.",  color:"#38bdf8", bg:"rgba(56,189,248,.14)"},
  resolved:      {label:"Resolvido",  color:"#34d399", bg:"rgba(52,211,153,.14)"},
};
const USERS0 = [
  {email:"admin@helgab.com.br",       pass:"escuta2026", role:"super",   name:"Marcio (Escuta Admin)", condoId:null},
  {email:"carlos@mantova.com.br",     pass:"mantova123", role:"sindico", name:"Carlos Mendes",         condoId:"mantova"},
  {email:"ana@brescia.com.br",        pass:"brescia123", role:"sindico", name:"Ana Paula Lima",        condoId:"brescia"},
  {email:"rafael@granreserve.com.br", pass:"reserve123", role:"sindico", name:"Rafael Costa",          condoId:"granreserve"},
];

/* ─── UTILS ─── */
const uid   = () => "TK-" + Math.random().toString(36).slice(2,8).toUpperCase();
const tokFn = n  => n.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"") + "-" + Math.random().toString(36).slice(2,5);
const fmt   = ts => new Date(ts).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
const fmtD  = s  => new Date(s).toLocaleDateString("pt-BR",{day:"2-digit",month:"short",year:"numeric"});
const fmtF  = s  => new Date(s).toLocaleDateString("pt-BR",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"});
const getAcc = c => c?.accent || ACCENTS[c?.ai] || "#EC4899";
const N = Date.now();

/* ─── DADOS ─── */
const CONDOS0 = [
  {id:"mantova",    name:"Mantova",     emoji:"🏢",type:"Residencial",ai:0,token:"mantova",    address:"Av. Brasil, 1200 – Campinas/SP",           adminEmail:"carlos@mantova.com.br",     adminWhats:"5519991110001",adminName:"Carlos Mendes", createdAt:new Date(N-30*864e5).toISOString(),
   faq:"Mantova: Academia 6h-22h. Salão sex/sáb/dom (reservar 72h antes). Piscina ter-dom 8h-20h. 2 pets/unidade (porte médio/pequeno). Lixo seg/qua/sex (reciclável terças). Taxa vence dia 10. Silêncio após 22h. Obras: seg-sex 8h-17h. Síndico: Carlos Mendes."},
  {id:"brescia",    name:"Brescia",     emoji:"🌆",type:"Residencial",ai:1,token:"brescia",    address:"R. das Flores, 450 – Campinas/SP",          adminEmail:"ana@brescia.com.br",        adminWhats:"5519991110002",adminName:"Ana Paula Lima",createdAt:new Date(N-20*864e5).toISOString(),
   faq:"Brescia: Quadra esportiva 7h-21h (sem reserva). Salão reservar 7 dias antes pelo portal. Sem pets acima 15kg. Silêncio após 22h. Taxa vence dia 5. Obras: seg-sex 9h-17h. Síndica: Ana Paula Lima."},
  {id:"granreserve",name:"Gran Reserve",emoji:"🌿",type:"Residencial",ai:2,token:"gran-reserve",address:"Alameda das Palmeiras, 800 – Campinas/SP", adminEmail:"rafael@granreserve.com.br", adminWhats:"5519991110003",adminName:"Rafael Costa",  createdAt:new Date(N-10*864e5).toISOString(),
   faq:"Gran Reserve: 2 piscinas (adulto/infantil) 8h-20h. Academia 24h (acesso por biometria). 2 churrasqueiras (reservar pelo portal com 48h). Todos os pets permitidos. Taxa vence dia 15. Síndico: Rafael Costa."},
];
const TICKETS0 = [
  {id:"TK-A1B2",condoId:"mantova",    icon:"🔧",cat:"Manutenção e Reparos", status:"in-progress",esc:true, rating:null,createdAt:new Date(N-72e5).toISOString(),
   resident:{name:"João Silva",unit:"Apto 302",phone:"19991110010"},
   msgs:[{by:"user",text:"Lâmpada queimada no corredor do 3º andar.",ts:N-72e5},{by:"ai",text:"Solicitação registrada. Equipe técnica será acionada em breve.",ts:N-72e5+5e3},{by:"gestor",text:"Equipe acionada! Troca amanhã entre 9h e 12h. Obrigado!",ts:N-36e5}]},
  {id:"TK-C3D4",condoId:"mantova",    icon:"📋",cat:"Dúvidas sobre Regras",  status:"resolved",   esc:false,rating:5,  createdAt:new Date(N-864e5).toISOString(),
   resident:{name:"Maria Souza",unit:"Apto 501",phone:"19991110011"},
   msgs:[{by:"user",text:"Posso ter dois cachorros?",ts:N-864e5},{by:"ai",text:"Sim! O Mantova permite até 2 animais por unidade (porte médio/pequeno). Pets de grande porte precisam de aprovação em assembleia.",ts:N-864e5+4e3}]},
  {id:"TK-E5F6",condoId:"mantova",    icon:"🔊",cat:"Barulho e Conduta",     status:"open",        esc:true, rating:null,createdAt:new Date(N-18e5).toISOString(),
   resident:{name:"Carlos Lima",unit:"Torre B 204",phone:"19991110012"},
   msgs:[{by:"user",text:"Música alta no 402 desde as 23h.",ts:N-18e5},{by:"ai",text:"Reclamação registrada com prioridade. Síndico acionado.",ts:N-18e5+4e3}]},
  {id:"TK-G7H8",condoId:"brescia",    icon:"ℹ️", cat:"Informações Gerais",   status:"resolved",   esc:false,rating:4,  createdAt:new Date(N-2*864e5).toISOString(),
   resident:{name:"Paula Costa",unit:"Apto 101",phone:"19991110013"},
   msgs:[{by:"user",text:"Qual o horário da quadra?",ts:N-2*864e5},{by:"ai",text:"A quadra do Brescia funciona das 7h às 21h todos os dias, sem reserva!",ts:N-2*864e5+3e3}]},
  {id:"TK-I9J0",condoId:"brescia",    icon:"🔧",cat:"Manutenção e Reparos",  status:"open",        esc:true, rating:null,createdAt:new Date(N-36e4).toISOString(),
   resident:{name:"André Rocha",unit:"Apto 305",phone:"19991110014"},
   msgs:[{by:"user",text:"Portão da garagem não está abrindo.",ts:N-36e4},{by:"ai",text:"Emergência registrada. Equipe técnica acionada — estimativa: 1 hora.",ts:N-36e4+4e3}]},
  {id:"TK-K1L2",condoId:"granreserve",icon:"💬",cat:"Sugestões e Elogios",   status:"resolved",   esc:false,rating:5,  createdAt:new Date(N-9e5).toISOString(),
   resident:{name:"Fernanda Alves",unit:"Casa 12",phone:"19991110015"},
   msgs:[{by:"user",text:"Adorei o novo paisagismo da entrada!",ts:N-9e5},{by:"ai",text:"Que ótimo! Vou registrar seu elogio e repassar à equipe responsável. Obrigado pelo carinho! ✅",ts:N-9e5+3e3}]},
];

/* ─── PROMPT IA ─── */
async function askClaude(msgs, faq, condoName, residentName) {
  const sys = `Você é Escuta, assistente de atendimento inteligente do condomínio ${condoName}.
Você está conversando com ${residentName || "um morador"}.

BASE DE CONHECIMENTO:
${faq}

═══════════════════════════════════════
O QUE VOCÊ CONSEGUE RESOLVER SOZINHO:
═══════════════════════════════════════
✅ Informações de horários, regras e regulamento
✅ Normas para pets, mudanças, obras e visitantes
✅ Como reservar espaços (salão, churrasqueira, quadra)
✅ Dúvidas sobre taxas, vencimentos e pagamento
✅ Orientações sobre coleta de lixo e reciclagem
✅ Sugestões e elogios (registrar e agradecer)
✅ Qualquer informação presente no FAQ acima

⚠️ ESCALAR APENAS NESTAS SITUAÇÕES:
• Manutenção física: vazamento, elétrica, elevador, portão, estrutura, infiltração
• Solicitação de documentos: declaração, boleto 2ª via, ata, contrato
• Conflito entre moradores que exige intervenção do síndico
• Reclamação formal que precisa ser registrada oficialmente
• Aprovação de obras ou reformas na unidade
• Assuntos financeiros complexos: negociação de débito
• Emergências de qualquer tipo
• Quando o morador pedir explicitamente para falar com o síndico

═══════════════════════════════════════
COMPORTAMENTO:
═══════════════════════════════════════
- Responda SEMPRE em português brasileiro
- Tom: cordial, prestativo e profissional
- NUNCA use emojis nem símbolos decorativos. Escreva em texto puro.
- NUNCA use formatação markdown (nada de **, #, listas com - ou *). Apenas texto corrido.
- SEJA EXTREMAMENTE BREVE: responda em 1 frase. No máximo 2 frases muito curtas — nunca mais que isso.
- Não se apresente, não dê boas-vindas e não liste tudo que você pode fazer. Vá direto à resposta.
- NÃO use listas numeradas nem passo a passo. Resuma a orientação em uma única frase direta.
- Não repita o que o morador disse nem explique o óbvio. Responda só o que foi perguntado.
- Se o morador enviar foto/vídeo: agradeça e confirme que foi registrado
- Tente resolver completamente na primeira resposta
- Se foto/vídeo ajudou a entender o problema de manutenção: ainda use [ESCALAR]

TOKENS INTERNOS (nunca exiba ao usuário):
- Se RESOLVEU completamente: última linha = [RESOLVIDO]
- Se precisa ESCALAR: última linha = [ESCALAR]
- Use [ESCALAR] UMA ÚNICA VEZ por chamado. Se você JÁ escalou ou registrou nesta conversa (veja o histórico acima), NÃO escale de novo.
- NUNCA use [RESOLVIDO] num chamado que você escalou ao síndico — quem resolve é o síndico, não você.
- Se o morador apenas confirma ou agradece (ok, obrigado, valeu, blz, tá) depois de você já ter registrado/escalado: responda em 1 frase curta e cordial, SEM nenhum token.
- Se conversa em andamento sem resolução: nenhum token`;

  const apiMsgs = msgs
    .filter(m => m.by === "user" || m.by === "ai")
    .map(m => ({
      role: m.by === "user" ? "user" : "assistant",
      content: m.file
        ? `[${m.file.type === "image" ? "Imagem" : "Vídeo"} enviado: ${m.file.name}]`
        : (m.text || ""),
    }));

  const res = await fetch("/api/chat", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({system:sys, messages:apiMsgs}),
  });
  if (!res.ok) throw new Error("api_err");
  const d = await res.json();
  return d.content[0]?.text || "";
}

/* ─── NOTIFICAÇÃO SÍNDICO ─── */
function buildSummary(msgs) {
  const lines = (msgs||[])
    .filter(m => m.by === "user")
    .map(m => {
      if (m.file) return `[${m.file.type === "image" ? "Imagem" : "Vídeo"} enviado]`;
      return (m.text||"").trim();
    })
    .filter(Boolean);
  if (!lines.length) return "Sem detalhes informados.";
  // Remove primeiro item se for só a categoria (ex: "🔧 Manutenção e Reparos")
  const relevant = lines.length > 1 ? lines.slice(1) : lines;
  return relevant.join("\n");
}

function notifyResponsible(condo, ticket, resident) {
  const summary  = buildSummary(ticket.msgs);
  const resName  = resident?.name  || "—";
  const resUnit  = resident?.unit  || "—";
  const resPhone = resident?.phone || "—";

  // Mensagem WhatsApp com resumo da conversa
  const waLines = [
    "🏢 *Escuta — " + condo.name + "*",
    "📋 *Chamado: " + ticket.id + "*",
    "",
    "*Categoria:* " + ticket.icon + " " + ticket.cat,
    "*Morador:* " + resName,
    "*Unidade:* " + resUnit,
    "*Celular:* " + resPhone,
    "",
    "*Resumo da solicitação:*",
    "_" + summary + "_",
    "",
    "Acesse o painel Escuta para responder.",
  ];
  const waMsg = waLines.join("\n");

  // WhatsApp — nova aba (não navega para fora)
  if (condo.adminWhats) {
    window.open("https://wa.me/" + condo.adminWhats + "?text=" + encodeURIComponent(waMsg), "_blank");
  }

  // E-mail — nova aba (não usa window.location.href que sai da página)
  if (condo.adminEmail) {
    const subject = encodeURIComponent("[Escuta] Chamado " + ticket.id + " — " + ticket.cat);
    const emailBody = [
      "Olá " + condo.adminName + ",",
      "",
      "Um chamado foi escalado no " + condo.name + ".",
      "",
      "Chamado: " + ticket.id,
      "Categoria: " + ticket.icon + " " + ticket.cat,
      "",
      "Morador: " + resName,
      "Unidade: " + resUnit,
      "Celular: " + resPhone,
      "",
      "Resumo da solicitação:",
      summary,
      "",
      "Acesse o painel Escuta para responder.",
    ].join("\n");
    window.open("mailto:" + condo.adminEmail + "?subject=" + subject + "&body=" + encodeURIComponent(emailBody), "_blank");
  }
}

/* ─── TOAST (gerenciado externamente via prop) ─── */
function ToastContainer({ toasts }) {
  if (!toasts.length) return null;
  const styles = {
    success: {background:"rgba(52,211,153,.18)",  border:"1px solid rgba(52,211,153,.35)",  color:"#34d399"},
    warning: {background:"rgba(245,158,11,.18)",  border:"1px solid rgba(245,158,11,.35)",  color:"#f59e0b"},
    info:    {background:"rgba(236,72,153,.15)",   border:"1px solid rgba(236,72,153,.3)",    color:"#EC4899"},
    error:   {background:"rgba(239,68,68,.18)",   border:"1px solid rgba(239,68,68,.35)",   color:"#f87171"},
  };
  return (
    <div className="toast-wrap">
      {toasts.map(t => (
        <div key={t.id} className="toast-item" style={styles[t.type]||styles.info}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─── COMPONENTES BASE ─── */
function StatusTag({status}) {
  const s = SM[status]||SM.open;
  return <span className="tag" style={{background:s.bg,color:s.color}}><span style={{width:6,height:6,borderRadius:"50%",background:s.color,display:"inline-block"}}/>{s.label}</span>;
}
/* Categoria (emoji nos dados/textos) -> ícone Lucide na tela */
const CAT_ICON = {"🔧":Wrench,"🔊":Volume2,"📋":ClipboardList,"ℹ️":Info,"🚨":Siren,"💬":MessageSquare,"❓":HelpCircle};
function EIcon({e,size=18,...p}){ const I = CAT_ICON[e] || HelpCircle; return <I size={size} {...p}/>; }
function CatLabel({icon,name,size=16}){ return <span style={{display:"inline-flex",alignItems:"center",gap:6,verticalAlign:"middle"}}><EIcon e={icon} size={size}/>{name}</span>; }
/* Avatar de condomínio: ícone de prédio na cor do condomínio */
function CondoAvatar({condo,size=40,radius=11,icon=20}){
  const a=getAcc(condo);
  return (
    <div style={{width:size,height:size,borderRadius:radius,background:`${a}1f`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0,color:a}}>
      {condo.logo ? <img src={condo.logo} style={{width:"100%",height:"100%",objectFit:"contain"}} alt=""/> : <Building2 size={icon}/>}
    </div>
  );
}
function StatCard({icon,label,value,color,sub}) {
  return (
    <div className="card" style={{display:"flex",gap:12,alignItems:"center"}}>
      <div style={{width:44,height:44,borderRadius:11,background:`${color}20`,color:color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{icon}</div>
      <div>
        <div style={{fontSize:24,fontWeight:800,lineHeight:1,color:"#20264B"}}>{value}</div>
        <div style={{fontSize:13,color:"#6B7280",marginTop:2}}>{label}</div>
        {sub && <div style={{fontSize:11,color,marginTop:1}}>{sub}</div>}
      </div>
    </div>
  );
}

/* ─── BOLHA DE MENSAGEM (reutilizável) ─── */
function MsgBubble({m, accentColor}) {
  const isUser   = m.by === "user";
  const isGestor = m.by === "gestor";
  const bg = isUser ? (accentColor||"#EC4899") : isGestor ? "#ECEDF3" : "#EEF0F6";
  const textColor = isUser ? "#000" : "#20264B";
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:isUser?"flex-end":"flex-start"}}>
      {isGestor && <div style={{fontSize:11,color:"#6B7280",marginBottom:3,display:"flex",alignItems:"center",gap:4}}><Headset size={12}/>Atendente</div>}
      {m.by==="ai" && <div style={{fontSize:11,color:"#6B7280",marginBottom:3,display:"flex",alignItems:"center",gap:4}}><Bot size={12}/>Escuta IA</div>}
      <div style={{maxWidth:"82%",borderRadius:isUser?"14px 14px 3px 14px":"14px 14px 14px 3px",overflow:"hidden",background:bg}}>
        {m.file ? (
          <div style={{padding:"6px 6px 3px 6px"}}>
            {m.file.type==="image"
              ? <img src={m.file.dataUrl} alt={m.file.name} style={{width:"100%",maxWidth:240,borderRadius:8,display:"block"}}/>
              : <video src={m.file.dataUrl} controls style={{width:"100%",maxWidth:240,borderRadius:8,display:"block"}}/>}
            <div style={{fontSize:10,color:isUser?"rgba(0,0,0,.5)":"#9CA3AF",padding:"4px 4px 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.file.name}</div>
          </div>
        ) : (
          <div style={{padding:"9px 13px",fontSize:14,lineHeight:1.5,color:textColor}}>{m.text}</div>
        )}
      </div>
      <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{fmt(m.ts)}</div>
    </div>
  );
}

/* ─── BOLHA GESTOR (painel admin) ─── */
function MsgBubbleAdmin({m}) {
  const isUser = m.by==="user";
  const bg = isUser?"#EEF0F6":m.by==="ai"?"#EEF2FF":"#E0E7FF";
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:isUser?"flex-end":"flex-start"}}>
      <div style={{fontSize:11,color:"#9CA3AF",marginBottom:3}}>
        {isUser?"Morador":m.by==="ai"?"Escuta IA":"Você"} · {fmt(m.ts)}
      </div>
      <div style={{maxWidth:"80%",borderRadius:9,overflow:"hidden",background:bg}}>
        {m.file ? (
          <div style={{padding:"6px 6px 3px 6px"}}>
            {m.file.type==="image"
              ? <img src={m.file.dataUrl} alt={m.file.name} style={{width:"100%",maxWidth:220,borderRadius:6,display:"block"}}/>
              : <video src={m.file.dataUrl} controls style={{width:"100%",maxWidth:220,borderRadius:6,display:"block"}}/>}
            <div style={{fontSize:10,color:"#9CA3AF",padding:"4px 2px 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.file.name}</div>
          </div>
        ) : (
          <div style={{padding:"8px 12px",fontSize:13,lineHeight:1.5,color:"#20264B"}}>{m.text}</div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   LOGIN
════════════════════════════════════ */
/* ════════════════════════════════════
   CONVITE (onboarding — criar a própria senha)
════════════════════════════════════ */
function InviteScreen({token}) {
  const [info, setInfo]   = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | invalid | done
  const [pass, setPass]   = useState("");
  const [pass2, setPass2] = useState("");
  const [err, setErr]     = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(()=>{
    fetch(`/api/invite/${token}`).then(r=>r.ok?r.json():Promise.reject())
      .then(d=>{ setInfo(d); setStatus("ok"); }).catch(()=>setStatus("invalid"));
  },[token]);

  const submit = async () => {
    setErr("");
    if (pass.length<4) { setErr("A senha precisa de pelo menos 4 caracteres."); return; }
    if (pass!==pass2)  { setErr("As senhas não coincidem."); return; }
    setSaving(true);
    const res = await fetch(`/api/invite/${token}`, {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({password:pass})}).catch(()=>null);
    setSaving(false);
    if (res && res.ok) setStatus("done");
    else setErr("Não foi possível salvar. O convite pode ter expirado.");
  };
  const lbl = {display:"block",fontSize:12.5,fontWeight:700,color:"#4B5563",marginBottom:7};

  return (
    <div style={{minHeight:"100vh",background:"#F7F8FC",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"Plus Jakarta Sans,sans-serif"}}>
      <div style={{width:"100%",maxWidth:400,background:"#fff",borderRadius:22,boxShadow:"0 18px 54px rgba(31,37,64,.12)",padding:"34px 30px"}} className="fu">
        <div style={{display:"flex",justifyContent:"center",marginBottom:18}}><LogoSVG width={150} color="#20264B" bgColor="#ffffff"/></div>
        {status==="loading" && <p style={{textAlign:"center",color:"#6B7280"}}>Carregando…</p>}
        {status==="invalid" && (<>
          <h2 style={{fontSize:20,fontWeight:800,color:"#20264B",textAlign:"center"}}>Convite inválido</h2>
          <p style={{fontSize:14,color:"#6B7280",textAlign:"center",marginTop:8,lineHeight:1.6}}>Este link é inválido ou já expirou. Peça um novo ao administrador.</p>
        </>)}
        {status==="done" && (<>
          <div style={{display:"flex",justifyContent:"center",marginBottom:10,color:"#22c55e"}}><CheckCircle2 size={44}/></div>
          <h2 style={{fontSize:20,fontWeight:800,color:"#20264B",textAlign:"center"}}>Senha criada!</h2>
          <p style={{fontSize:14,color:"#6B7280",textAlign:"center",marginTop:8,marginBottom:20}}>Pronto{info?.name?`, ${info.name.split(" ")[0]}`:""}! Agora você já pode entrar no painel.</p>
          <a href="/" style={{textDecoration:"none"}}><button className="mbtn" style={{width:"100%",justifyContent:"center"}}>Ir para o login →</button></a>
        </>)}
        {status==="ok" && (<>
          <h2 style={{fontSize:22,fontWeight:800,color:"#20264B",textAlign:"center"}}>Crie sua senha</h2>
          <p style={{fontSize:14,color:"#6B7280",textAlign:"center",marginTop:6,marginBottom:22,lineHeight:1.6}}>Olá, {info?.name}! Defina sua senha de acesso ao painel<br/><span style={{fontWeight:600,color:"#20264B"}}>{info?.email}</span></p>
          <label style={lbl}>Nova senha</label>
          <input className="inp" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" style={{marginBottom:12}}/>
          <label style={lbl}>Confirme a senha</label>
          <input className="inp" type="password" value={pass2} onChange={e=>setPass2(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="••••••••" style={{marginBottom:13}}/>
          {err && <div style={{fontSize:13,color:"#EF4444",marginBottom:12}}>⚠ {err}</div>}
          <button className="mbtn" onClick={submit} disabled={saving} style={{width:"100%",justifyContent:"center"}}>{saving?<><span className="spin-i"/>Salvando…</>:"Criar senha e acessar"}</button>
        </>)}
      </div>
    </div>
  );
}

function LoginScreen({onLogin}) {
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [showP,   setShowP]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [shake,   setShake]   = useState(false);

  const doLogin = async () => {
    if (!email || !pass) { setError("Preencha e-mail e senha."); setShake(true); setTimeout(()=>setShake(false),500); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/login", { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ email: email.trim(), password: pass }) });
      if (res.ok) { onLogin(await res.json()); }
      else { setError("E-mail ou senha inválidos."); setLoading(false); setShake(true); setTimeout(()=>setShake(false),500); }
    } catch {
      setError("Erro de conexão. Tente novamente."); setLoading(false); setShake(true); setTimeout(()=>setShake(false),500);
    }
  };

  return (
    <div className="lg-wrap">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .lg-wrap{min-height:100vh;background:#fff;font-family:'Plus Jakarta Sans',sans-serif;color:#20264B;display:flex;flex-direction:column;position:relative;overflow:hidden;}
        .lg-wrap::before{content:"";position:absolute;top:-170px;right:-120px;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(219,234,254,.7),transparent 66%);pointer-events:none;}
        .lg-top{display:flex;align-items:center;justify-content:space-between;padding:22px 44px;position:relative;z-index:2;}
        .lg-toplink{font-size:13px;font-weight:600;color:#8B5CF6;}
        .lg-main{flex:1;display:flex;align-items:center;gap:40px;padding:6px 56px 48px;max-width:1180px;margin:0 auto;width:100%;position:relative;z-index:2;}
        .lg-left{flex:1;max-width:432px;animation:lgUp .6s cubic-bezier(.2,.8,.2,1) both;}
        .lg-right{flex:1.05;position:relative;display:flex;align-items:center;justify-content:center;min-height:440px;}
        @keyframes lgUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
        .lg-eyebrow{display:inline-block;font-size:13px;font-weight:700;color:#8B5CF6;background:rgba(139,92,246,.1);padding:6px 13px;border-radius:999px;}
        .lg-h1{font-size:clamp(34px,4.4vw,52px);font-weight:800;line-height:1.05;letter-spacing:-.02em;color:#20264B;margin:16px 0 0;}
        .lg-h1 span{color:#EC4899;}
        .lg-sub{font-size:16px;line-height:1.6;color:#6B7280;margin:14px 0 24px;max-width:380px;}
        .lg-label{display:block;font-size:12.5px;font-weight:700;color:#4B5563;margin-bottom:7px;}
        .lg-field{margin-bottom:14px;}
        .lg-ipwrap{position:relative;}
        .lg-input{width:100%;background:#F6F7FB;border:1.5px solid #ECEDF3;border-radius:13px;padding:13px 15px;font-size:15px;color:#20264B;font-family:'Plus Jakarta Sans',sans-serif;outline:none;transition:.2s;}
        .lg-input::placeholder{color:#9CA3AF;}
        .lg-input:focus{border-color:#EC4899;background:#fff;box-shadow:0 0 0 4px rgba(236,72,153,.12);}
        .lg-field.err .lg-input{border-color:#EF4444;}
        .lg-eye{position:absolute;right:8px;top:50%;transform:translateY(-50%);background:none;border:none;color:#9CA3AF;cursor:pointer;font-size:16px;padding:6px;line-height:1;}
        .lg-btn{width:100%;margin-top:8px;background:#EC4899;color:#fff;border:none;border-radius:999px;padding:14px 20px;font-size:15px;font-weight:700;font-family:'Plus Jakarta Sans',sans-serif;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;transition:transform .15s,box-shadow .25s,background .2s;box-shadow:0 10px 26px rgba(236,72,153,.32);}
        .lg-btn:hover:not(:disabled){background:#DB2777;transform:translateY(-2px);box-shadow:0 16px 34px rgba(236,72,153,.42);}
        .lg-btn:disabled{opacity:.6;cursor:not-allowed;}
        .lg-ghost{width:100%;background:#fff;border:1.5px solid #ECEDF3;border-radius:999px;padding:11px;color:#6B7280;font-size:13px;font-weight:600;cursor:pointer;margin-top:16px;transition:.2s;font-family:'Plus Jakarta Sans',sans-serif;}
        .lg-ghost:hover{border-color:#8B5CF6;color:#8B5CF6;}
        .lg-demo{margin-top:10px;display:flex;flex-direction:column;gap:8px;}
        .lg-row{width:100%;background:#FAFAFE;border:1.5px solid #EEF0F6;border-radius:13px;padding:11px 14px;text-align:left;cursor:pointer;transition:.15s;font-family:'Plus Jakarta Sans',sans-serif;}
        .lg-row:hover{border-color:#EC4899;background:#fff;box-shadow:0 6px 16px rgba(31,37,64,.06);}
        .lg-err{font-size:13px;color:#EF4444;margin:0 0 12px;}
        .lg-blob{position:absolute;border-radius:50%;pointer-events:none;z-index:1;}
        .lg-blob.a{width:420px;height:420px;background:radial-gradient(circle,rgba(199,210,254,.75),transparent 66%);top:0;right:2%;}
        .lg-blob.b{width:320px;height:320px;background:radial-gradient(circle,rgba(251,207,232,.75),transparent 66%);bottom:0;left:6%;}
        .lg-illus{position:relative;z-index:2;width:100%;max-width:520px;animation:lgFloat 5s ease-in-out infinite;}
        @keyframes lgFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        @media (max-width:900px){
          .lg-top{padding:18px 24px;}
          .lg-main{flex-direction:column;gap:14px;padding:6px 24px 48px;}
          .lg-left{max-width:440px;width:100%;order:2;}
          .lg-right{order:1;min-height:auto;width:100%;}
          .lg-illus{max-width:280px;}
          .lg-toplink{display:none;}
        }
      `}</style>

      <div className="lg-top">
        <LogoSVG width={140} color="#20264B" bgColor="#ffffff"/>
        <div className="lg-toplink">Atendimento inteligente para condomínios</div>
      </div>

      <div className="lg-main">
        {/* ── ESQUERDA: texto + formulário ── */}
        <div className={`lg-left ${shake?"shk":""}`}>
          <div className="lg-eyebrow">Bem-vindo de volta 👋</div>
          <h1 className="lg-h1">Acesse seu <span>painel</span></h1>
          <p className="lg-sub">Acompanhe as solicitações dos moradores e responda em segundos, num só lugar.</p>

          <div className={`lg-field ${error?"err":""}`}>
            <label className="lg-label">E-mail</label>
            <input className="lg-input" type="email" placeholder="seu@email.com.br" value={email} onChange={e=>{setEmail(e.target.value);setError("");}} onKeyDown={e=>e.key==="Enter"&&doLogin()} autoComplete="email"/>
          </div>
          <div className={`lg-field ${error?"err":""}`}>
            <label className="lg-label">Senha</label>
            <div className="lg-ipwrap">
              <input className="lg-input" type={showP?"text":"password"} placeholder="••••••••" value={pass} onChange={e=>{setPass(e.target.value);setError("");}} onKeyDown={e=>e.key==="Enter"&&doLogin()} style={{paddingRight:42}}/>
              <button type="button" className="lg-eye" onClick={()=>setShowP(p=>!p)}>{showP?<EyeOff size={17}/>:<Eye size={17}/>}</button>
            </div>
          </div>
          {error && <div className="lg-err">⚠ {error}</div>}
          <button className="lg-btn" onClick={()=>doLogin()} disabled={loading}>
            {loading?<><span className="spin-i" style={{borderColor:"rgba(255,255,255,.4)",borderTopColor:"#fff"}}/>Entrando…</>:<>Entrar →</>}
          </button>
        </div>

        {/* ── DIREITA: ilustração ── */}
        <div className="lg-right">
          <div className="lg-blob a"/><div className="lg-blob b"/>
          <img className="lg-illus" src="https://illustrations.popsy.co/purple/communication.svg" alt="Comunicação entre moradores e síndico"/>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   QR CODE
════════════════════════════════════ */
/* ════════════════════════════════════
   GERADOR DE PLACA
════════════════════════════════════ */

function rr(ctx,x,y,w,h,r){ctx.beginPath();ctx.moveTo(x+r,y);ctx.lineTo(x+w-r,y);ctx.quadraticCurveTo(x+w,y,x+w,y+r);ctx.lineTo(x+w,y+h-r);ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);ctx.lineTo(x+r,y+h);ctx.quadraticCurveTo(x,y+h,x,y+h-r);ctx.lineTo(x,y+r);ctx.quadraticCurveTo(x,y,x+r,y);ctx.closePath();}

function loadQRLib(){
  return new Promise(resolve=>{
    if(window._QRLoaded){resolve();return;}
    const s=document.createElement('script');
    s.src='https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    s.onload=()=>{window._QRLoaded=true;resolve();}; s.onerror=()=>resolve();
    document.head.appendChild(s);
  });
}

function generateQRDataUrl(text,size){
  return new Promise(resolve=>{
    const div=document.createElement('div');
    div.style.cssText='visibility:hidden;position:fixed;left:-9999px;';
    document.body.appendChild(div);
    try{
      new window.QRCode(div,{text,width:size,height:size,correctLevel:window.QRCode.CorrectLevel.M});
      setTimeout(()=>{
        const cv=div.querySelector('canvas');
        const du=cv?cv.toDataURL('image/png'):null;
        document.body.removeChild(div); resolve(du);
      },200);
    }catch(e){document.body.removeChild(div);resolve(null);}
  });
}

function loadImgEl(src){
  return new Promise(resolve=>{
    if(!src){resolve(null);return;}
    const img=new Image();
    img.onload=()=>resolve(img); img.onerror=()=>resolve(null);
    setTimeout(()=>resolve(null),6000);
    img.src=src;
  });
}

function PlacaGenerator({condo}){
  const [qrDataUrl,setQrDataUrl]=useState(null);
  const [status,setStatus]=useState('idle');
  const origin=typeof window!=="undefined"?window.location.origin:"";
  const url=`${origin}/c/${condo.token}`;
  const a=getAcc(condo);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      await loadQRLib();
      if(cancelled)return;
      const du=await generateQRDataUrl(url,320);
      if(!cancelled)setQrDataUrl(du);
    })();
    return()=>{cancelled=true;};
  },[url]);

  /* ════════════════════════════
     CONSTANTES DE LAYOUT
     Mesmas proporções na prévia e no canvas
  ════════════════════════════ */
  // Canvas: W=1748, H=2480 (A5 @ 300dpi)
  // Prévia: PW=190, PH=270  (mesma proporção)
  // Todas as posições verticais são % de H;
  // posições horizontais são % de W.
  const LAYOUT = {
    hdrBase:    0.28,   // topo da curva (% de H)
    wavePeak:   0.34,   // fundo da curva / início do body (% de H)
    qrPad:      0.025,  // espaço entre curva e QR (% de H)
    qrSize:     0.52,   // tamanho do QR (% de W)
    textGap:    0.030,  // espaço entre QR e texto (% de H)
    mainFont:   0.047,  // fonte principal (% de W)
    mainLH:     1.35,   // line-height
    subFont:    0.030,  // fonte subtítulo (% de W)
    subLH:      1.35,
    subGap:     0.022,  // espaço entre texto e subtítulo (% de H)
    divY:       0.92,   // posição do divisor (% de H)
    logoW:      0.65,   // largura do logo Escuta no header (% de W)
  };

  const download=async()=>{
    setStatus('loading');
    try{
      await loadQRLib();
      const qrHD=await generateQRDataUrl(url,900);
      if(!qrHD)throw new Error('QR falhou');

      /* ── Canvas A5 @ 300dpi ── */
      const W=1748, H=2480;
      const cv=document.createElement('canvas'); cv.width=W; cv.height=H;
      const ctx=cv.getContext('2d');

      const L=LAYOUT;
      const hdrBase   = Math.round(H*L.hdrBase);    // 694
      const wavePeak  = Math.round(H*L.wavePeak);   // 843
      const qrSize    = Math.round(W*L.qrSize);     // 909
      const qrX       = Math.round((W-qrSize)/2);
      const qrY       = wavePeak + Math.round(H*L.qrPad);  // 905
      const textBase  = qrY + qrSize + Math.round(H*L.textGap); // 1888
      const mainFont  = Math.round(W*L.mainFont);   // 82
      const mainLH    = Math.round(mainFont*L.mainLH); // 111
      const subFont   = Math.round(W*L.subFont);    // 52
      const subLH     = Math.round(subFont*L.subLH);  // 70
      const subGap    = Math.round(H*L.subGap);     // 55
      const divY      = Math.round(H*L.divY);       // 2282

      /* Fundo branco */
      ctx.fillStyle='#ffffff'; ctx.fillRect(0,0,W,H);

      /* Header colorido */
      ctx.fillStyle=a; ctx.fillRect(0,0,W,hdrBase);

      /* Curva branca (cobre área de hdrBase até wavePeak) */
      ctx.fillStyle='#ffffff';
      ctx.beginPath();
      ctx.moveTo(0,hdrBase);
      ctx.quadraticCurveTo(W/2,wavePeak,W,hdrBase);
      ctx.lineTo(W,H); ctx.lineTo(0,H); ctx.closePath(); ctx.fill();

      /* Logo Escuta branco no header (via SVG blob) */
      const logoW=Math.round(W*L.logoW);
      const logoResult=await loadLogoOnCanvas('white',a,logoW);
      if(logoResult){
        const lX=Math.round((W-logoResult.w)/2);
        const lY=Math.round(hdrBase/2-logoResult.h/2);
        ctx.drawImage(logoResult.img,lX,lY,logoResult.w,logoResult.h);
      }

      /* QR Code — caixa branca com sombra */
      const qrP=Math.round(W*0.04);
      ctx.shadowColor='rgba(0,0,0,0.11)'; ctx.shadowBlur=W*0.02; ctx.shadowOffsetY=W*0.008;
      ctx.fillStyle='#ffffff';
      rr(ctx,qrX-qrP,qrY-qrP,qrSize+qrP*2,qrSize+qrP*2,W*0.018); ctx.fill();
      ctx.shadowBlur=0; ctx.shadowOffsetY=0; ctx.shadowColor='transparent';
      const qrImg=await loadImgEl(qrHD);
      if(qrImg) ctx.drawImage(qrImg,qrX,qrY,qrSize,qrSize);

      /* Título — duas cores */
      ctx.textAlign='center';
      ctx.fillStyle='#1a1a1a'; ctx.font=`bold ${mainFont}px Arial,sans-serif`;
      ctx.fillText(`Ajude o ${condo.name}`, W/2, textBase+mainFont);
      ctx.fillStyle=a;
      ctx.fillText('a ser cada vez melhor', W/2, textBase+mainFont+mainLH);

      /* Linha de destaque sob o título */
      const accentY = textBase+mainFont+mainLH+Math.round(H*0.020);
      ctx.strokeStyle=a; ctx.lineWidth=W*0.007; ctx.lineCap='round';
      ctx.beginPath(); ctx.moveTo(W/2-W*0.06, accentY); ctx.lineTo(W/2+W*0.06, accentY); ctx.stroke();
      ctx.lineCap='butt';

      /* Instrução */
      ctx.fillStyle='#555'; ctx.font=`${subFont}px Arial,sans-serif`;
      const subBase=accentY+Math.round(H*0.014);
      ctx.fillText('Aponte a câmera do seu celular para o código', W/2, subBase+subFont);
      ctx.fillText('e envie sua sugestão ou solicitação.',          W/2, subBase+subFont+subLH);

      /* Rodapé — divisor + nome/logo do condomínio + assinatura (bem espaçado) */
      const footDivY = Math.round(H*0.905);
      ctx.strokeStyle='#e6e6e6'; ctx.lineWidth=W*0.0025;
      ctx.beginPath(); ctx.moveTo(W*0.18,footDivY); ctx.lineTo(W*0.82,footDivY); ctx.stroke();

      if(condo.logo){
        const li=await loadImgEl(condo.logo);
        if(li&&li.width>0){
          const lH=Math.round(H*0.05);
          const lW=Math.min(li.width*(lH/li.height),W*0.45);
          ctx.drawImage(li,Math.round((W-lW)/2),footDivY+Math.round(H*0.016),lW,lH);
        }
      }else{
        ctx.fillStyle='#1a1a1a'; ctx.font=`bold ${Math.round(W*0.034)}px Arial,sans-serif`; ctx.textAlign='center';
        ctx.fillText(condo.name, W/2, footDivY+Math.round(H*0.048));
      }
      ctx.fillStyle='#bbbbbb'; ctx.font=`${Math.round(W*0.019)}px Arial,sans-serif`;
      ctx.fillText('Atendimento inteligente · powered by Escuta', W/2, H-Math.round(H*0.026));

      /* Download */
      const dataUrl=cv.toDataURL('image/png');
      const link=document.createElement('a');
      link.href=dataUrl; link.download=`placa-${condo.name.replace(/\s+/g,'-')}.png`;
      link.style.display='none'; document.body.appendChild(link); link.click();
      setTimeout(()=>document.body.removeChild(link),500);
      setStatus('done'); setTimeout(()=>setStatus('idle'),3000);
    }catch(err){
      console.error('Placa error:',err);
      setStatus('error'); setTimeout(()=>setStatus('idle'),4000);
    }
  };

  /* ════════════════════════════
     PRÉVIA HTML — mesmas proporções
  ════════════════════════════ */
  const PW = 190;
  const PH = Math.round(PW * 2480/1748); // 270
  const L  = LAYOUT;

  // Posições em px (escala: PW=190 → W=1748)
  const pH   = PH;
  const pW   = PW;
  const pHdrBase   = Math.round(pH*L.hdrBase);      // 76
  const pWavePeak  = Math.round(pH*L.wavePeak);     // 92
  const pWaveH     = pWavePeak - pHdrBase;           // 16
  const pQrSize    = Math.round(pW*L.qrSize);       // 99
  const pQrY       = pWavePeak + Math.round(pH*L.qrPad); // 99
  const pQrX       = Math.round((pW-pQrSize)/2);
  const pTextBase  = pQrY + pQrSize + Math.round(pH*L.textGap); // 207
  const pMainFont  = Math.max(8, Math.round(pW*L.mainFont));  // 9
  const pMainLH    = Math.round(pMainFont*L.mainLH);  // 12
  const pSubFont   = Math.max(7, Math.round(pW*L.subFont));  // 6→7
  const pSubLH     = Math.round(pSubFont*L.subLH);
  const pSubGap    = Math.max(4, Math.round(pH*L.subGap));    // 6
  const pDivY      = Math.round(pH*L.divY);          // 249
  const pLogoW     = Math.round(pW*L.logoW);         // 124
  const pLogoH     = Math.round(pLogoW*102/390);     // 32
  const pLogoTop   = Math.round(pHdrBase/2 - pLogoH/2); // 22

  return(
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      <div style={{display:'flex',gap:20,flexWrap:'wrap',alignItems:'flex-start'}}>

        {/* ── PRÉVIA FIEL ── */}
        <div style={{flexShrink:0}}>
          <p style={{fontSize:11,color:'#6B7280',fontWeight:600,letterSpacing:'1px',marginBottom:10}}>PRÉVIA EXATA DA PLACA</p>

          <div style={{width:pW,height:pH,background:'#fff',borderRadius:14,overflow:'hidden',
                       boxShadow:`0 14px 44px ${a}55`,position:'relative',flexShrink:0}}>

            {/* Header colorido */}
            <div style={{position:'absolute',top:0,left:0,right:0,height:pHdrBase,background:a}}/>

            {/* Logo Escuta no header */}
            <div style={{position:'absolute',top:pLogoTop,left:0,right:0,
                         display:'flex',justifyContent:'center',zIndex:2}}>
              <LogoSVG width={pLogoW} color="white" bgColor={a}/>
            </div>

            {/* Curva SVG — mesma forma do canvas */}
            <div style={{position:'absolute',top:pHdrBase,left:0,right:0,lineHeight:0,zIndex:1}}>
              <svg viewBox={`0 0 ${pW} ${pWaveH}`} style={{width:'100%',display:'block'}}>
                <path d={`M0,0 Q${pW/2},${pWaveH} ${pW},0 L${pW},${pWaveH} L0,${pWaveH} Z`} fill="#ffffff"/>
              </svg>
            </div>

            {/* QR Code */}
            <div style={{position:'absolute',top:pQrY-3,left:pQrX-3,
                         background:'#fff',borderRadius:5,padding:3,
                         boxShadow:'0 2px 10px rgba(0,0,0,.12)',zIndex:2}}>
              {qrDataUrl
                ? <img src={qrDataUrl} width={pQrSize} height={pQrSize}
                       alt="QR" style={{display:'block',borderRadius:2}}/>
                : <div style={{width:pQrSize,height:pQrSize,display:'flex',alignItems:'center',
                               justifyContent:'center',color:'#6B7280',fontSize:8}}>QR…</div>
              }
            </div>

            {/* Texto principal */}
            <div style={{position:'absolute',top:pTextBase,left:4,right:4,textAlign:'center',zIndex:2}}>
              <div style={{fontSize:pMainFont,fontWeight:700,lineHeight:L.mainLH}}>
                <span style={{color:'#1a1a1a'}}>Ajude o {condo.name}</span><br/><span style={{color:a}}>a ser cada vez melhor</span>
              </div>
              <div style={{width:pW*0.12,height:2,background:a,borderRadius:2,margin:`${Math.max(3,Math.round(pH*0.016))}px auto`}}/>
              <div style={{fontSize:pSubFont,color:'#666',lineHeight:L.subLH}}>
                Aponte a câmera do celular para o código<br/>e envie sua sugestão ou solicitação.
              </div>
            </div>

            {/* Divisor */}
            <div style={{position:'absolute',top:pDivY,left:pW*0.12,right:pW*0.12,
                         height:1,background:'#20264B',zIndex:2}}/>

            {/* Logo do condo */}
            <div style={{position:'absolute',top:pDivY+4,bottom:4,left:4,right:4,
                         display:'flex',alignItems:'center',justifyContent:'center',zIndex:2}}>
              {condo.logo
                ? <img src={condo.logo} alt="logo"
                       style={{maxHeight:pH-pDivY-8,maxWidth:pW*0.6,objectFit:'contain'}}/>
                : <span style={{fontSize:Math.max(7,Math.round(pW*0.045)),fontWeight:700,color:'#1a1a1a'}}>
                    {condo.name}
                  </span>
              }
            </div>
          </div>

          <p style={{fontSize:10,color:'#9CA3AF',marginTop:7,textAlign:'center',lineHeight:1.5}}>
            A5 · 1748×2480px · 300dpi
          </p>
        </div>

        {/* Painel direito */}
        <div style={{flex:1,minWidth:190,display:'flex',flexDirection:'column',gap:12}}>
          <div className="card" style={{padding:14}}>
            <p style={{fontSize:11,color:'#6B7280',fontWeight:600,letterSpacing:'1px',marginBottom:8}}>LOGO DO CONDOMÍNIO</p>
            {condo.logo
              ? <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <img src={condo.logo} style={{height:40,maxWidth:90,objectFit:'contain',borderRadius:6,border:'1px solid #ECEDF3'}} alt="logo"/>
                  <div><div style={{fontSize:12,color:'#34d399',fontWeight:600}}>✓ Configurado</div><div style={{fontSize:11,color:'#6B7280',marginTop:2}}>Aparece na base da placa</div></div>
                </div>
              : <div><p style={{fontSize:12,color:'#f59e0b',marginBottom:4}}>⚠ Sem logo</p><p style={{fontSize:11,color:'#9CA3AF'}}>Edite o condomínio para adicionar.</p></div>
            }
          </div>
          <div className="card" style={{padding:14}}>
            <p style={{fontSize:11,color:'#6B7280',fontWeight:600,letterSpacing:'1px',marginBottom:8}}>URL DO QR CODE</p>
            <div style={{background:'#F7F8FC',border:'1px solid #ECEDF3',borderRadius:8,padding:'8px 12px',fontSize:11,color:'#EC4899',wordBreak:'break-all',fontFamily:'monospace'}}>{url}</div>
          </div>
          <div className="card" style={{padding:14}}>
            <p style={{fontSize:11,color:'#6B7280',fontWeight:600,letterSpacing:'1px',marginBottom:10}}>ESPECIFICAÇÕES</p>
            {[['📐','Formato','A5 retrato (148×210mm)'],['🖨','Resolução','300dpi — ideal para gráfica'],['⚡','QR','Gerado localmente, sem rede'],['🎨','Design','Segue a cor do condomínio']].map(([ic,l,v],i)=>(
              <div key={i} style={{display:'flex',gap:9,marginBottom:8,alignItems:'flex-start'}}>
                <span style={{fontSize:14,flexShrink:0}}>{ic}</span>
                <div><div style={{fontSize:12,color:'#20264B',fontWeight:500}}>{l}</div><div style={{fontSize:11,color:'#6B7280'}}>{v}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Jornada */}
      <div className="card" style={{padding:14}}>
        <p style={{fontSize:11,color:'#6B7280',fontWeight:600,letterSpacing:'1px',marginBottom:12}}>JORNADA DO MORADOR</p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))',gap:10}}>
          {[['🪧','Placa na portaria','QR em local visível'],['📱','Escaneia','Câmera abre o portal'],['💬','IA atende','Resolve ou escala'],['📲','Síndico notificado','WhatsApp + e-mail']].map(([ic,t,d],i)=>(
            <div key={i} style={{textAlign:'center',padding:'10px 6px',background:'#F7F8FC',borderRadius:10,border:'1px solid #ECEDF3'}}>
              <div style={{fontSize:22,marginBottom:5}}>{ic}</div>
              <div style={{fontSize:11,fontWeight:700,color:a,marginBottom:3}}>{t}</div>
              <div style={{fontSize:10,color:'#6B7280',lineHeight:1.4}}>{d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Botão */}
      <button onClick={download} disabled={status==='loading'||!qrDataUrl} className="mbtn"
        style={{width:'100%',justifyContent:'center',padding:'15px',fontSize:15,
          background:status==='done'?'linear-gradient(135deg,#34d399,#10b981)':status==='error'?'linear-gradient(135deg,#ef4444,#dc2626)':`linear-gradient(135deg,${a},${a}bb)`}}>
        {status==='loading' && <><span className="spin-i"/>  Gerando placa…</>}
        {status==='done'    && '✓ Placa baixada com sucesso!'}
        {status==='error'   && '⚠ Erro — tente novamente'}
        {status==='idle'    && (!qrDataUrl?'⏳ Preparando QR Code…':'⬇ Baixar Placa (A5 · 300dpi)')}
      </button>
      <p style={{fontSize:11,color:'#9CA3AF',textAlign:'center',marginTop:-8}}>PNG profissional para gráficas.</p>
    </div>
  );
}


function QRSection({condo, onSimulate}) {
  const origin = typeof window!=="undefined"?window.location.origin:"";
  const url   = `${origin}/c/${condo.token}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(url)}&color=1a1a1a&bgcolor=ffffff&margin=12`;
  const qrHD  = `https://api.qrserver.com/v1/create-qr-code/?size=900x900&data=${encodeURIComponent(url)}&margin=24`;
  const a = getAcc(condo);
  const [copied, setCopied] = useState(false);

  const download = async () => {
    try { const r=await fetch(qrHD);const b=await r.blob();const el=document.createElement("a");el.href=URL.createObjectURL(b);el.download=`qr-${condo.name}.png`;el.click(); }
    catch { window.open(qrHD,"_blank"); }
  };
  const copyUrl = () => {
    navigator.clipboard.writeText(url).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);});
  };

  const STEPS = [
    {icon:"🪧",title:"Placa física",     desc:"QR Code exposto na portaria, elevador ou área de lazer"},
    {icon:"📱",title:"Morador escaneia", desc:"Câmera do celular detecta o QR — sem instalar app"},
    {icon:"💬",title:"Portal abre",      desc:"Browser abre o portal já configurado para este condomínio"},
    {icon:"🤖",title:"IA atende",        desc:"IA resolve ou escala — síndico recebe notificação automática"},
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div className="card" style={{padding:18}}>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:16}}>JORNADA DO MORADOR</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
          {STEPS.map((s,i)=>(
            <div key={i}>
              <div style={{width:34,height:34,borderRadius:10,background:`${a}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,marginBottom:8}}>{s.icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:a,letterSpacing:".5px",marginBottom:3}}>PASSO {i+1} — {s.title.toUpperCase()}</div>
              <div style={{fontSize:12,color:"#6B7280",lineHeight:1.5}}>{s.desc}</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:14,padding:"10px 14px",background:"rgba(236,72,153,.07)",border:"1px solid rgba(236,72,153,.18)",borderRadius:10,fontSize:13,color:"#6B7280"}}>
          💡 <strong style={{color:"#20264B"}}>Em produção:</strong> o QR aponta para o domínio onde o painel está hospedado. O token no link (<code style={{color:"#EC4899"}}>/c/{condo.token}</code>) identifica o condomínio e abre o portal do morador automaticamente.
        </div>
      </div>
      <div style={{display:"flex",gap:20,flexWrap:"wrap",alignItems:"flex-start"}}>
        <div style={{flexShrink:0,width:230}}>
          <PlacaGenerator condo={condo}/>
          {onSimulate && <button onClick={onSimulate} className="gbtn" style={{width:"100%",justifyContent:"center",marginTop:10,fontSize:13}}>Simular Escaneamento</button>}
        </div>
        <div style={{flex:1,minWidth:220,display:"flex",flexDirection:"column",gap:12}}>
          <div className="card" style={{padding:14}}>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:8}}>URL DO MORADOR</p>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <div style={{flex:1,background:"#F7F8FC",border:"1px solid #ECEDF3",borderRadius:8,padding:"9px 13px",fontSize:12,color:"#EC4899",wordBreak:"break-all",fontFamily:"monospace",lineHeight:1.5}}>{url}</div>
              <button onClick={copyUrl} style={{flexShrink:0,background:copied?"rgba(52,211,153,.15)":"#ECEDF3",border:`1px solid ${copied?"#34d399":"#ECEDF3"}`,borderRadius:8,padding:"8px 12px",color:copied?"#34d399":"#6B7280",fontSize:12,cursor:"pointer",fontFamily:"Plus Jakarta Sans,sans-serif",transition:"all .2s",whiteSpace:"nowrap"}}>
                {copied?"✓ Copiado":"Copiar"}
              </button>
            </div>
          </div>
          <div className="card" style={{padding:14}}>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:12}}>IMPRESSÃO</p>
            {[[Ruler,"Tamanho","A5 (15×21cm) ou maior"],[Printer,"Material","Papel couché ou vinilado"],[ShieldCheck,"Proteção","Plastifique para uso externo"],[MapPin,"Posição","Portaria, elevadores, áreas comuns"]].map(([Ic,l,v],i)=>(
              <div key={i} style={{display:"flex",gap:10,marginBottom:9,alignItems:"flex-start"}}>
                <Ic size={16} style={{flexShrink:0,color:"#EC4899",marginTop:1}}/>
                <div><div style={{fontSize:12,color:"#20264B",fontWeight:500}}>{l}</div><div style={{fontSize:11,color:"#6B7280",marginTop:1}}>{v}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   MODAL CRIAR CONDOMÍNIO
════════════════════════════════════ */
function CreateModal({onClose,onCreate}) {
  const [f,setF]=useState({name:"",type:"Residencial",address:"",email:"",pass:"",whats:"",manager:"",emoji:"🏢",ai:0});
  const [loading,setLoading]=useState(false);
  const set=(k,v)=>setF(p=>({...p,[k]:v}));
  const ok=f.name.trim()&&f.email.trim(); // senha é opcional (pode enviar link de convite)

  const submit=()=>{
    if(!ok)return;setLoading(true);
    setTimeout(()=>{
      const cid=tokFn(f.name).split("-").slice(0,-1).join("-")||uid();
      onCreate({
        id:cid,name:f.name,emoji:f.emoji,type:f.type,ai:f.ai,token:cid,
        address:f.address,adminEmail:f.email,adminName:f.manager,adminWhats:f.whats.replace(/[^0-9]/g,""),
        createdAt:new Date().toISOString(),
        faq:`${f.name}: Configure o FAQ deste espaço no painel de administração.`,
        _newUser:{email:f.email,pass:f.pass,role:"sindico",name:f.manager,condoId:null},
      });
      setLoading(false);onClose();
    },800);
  };

  return (
    <div className="mbg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal fu">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div><h2 style={{fontSize:19,fontWeight:700,color:"#20264B"}}>Novo Condomínio</h2><p style={{fontSize:13,color:"#6B7280",marginTop:3}}>Configure o espaço e acesso do síndico</p></div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B7280",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:8}}>ÍCONE</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
          {EMOJIS.map(e=><button key={e} onClick={()=>set("emoji",e)} style={{width:37,height:37,borderRadius:9,border:`2px solid ${f.emoji===e?"#EC4899":"#ECEDF3"}`,background:f.emoji===e?"rgba(236,72,153,.15)":"transparent",fontSize:17,cursor:"pointer"}}>{e}</button>)}
        </div>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:8}}>COR</p>
        <div style={{display:"flex",gap:8,marginBottom:18}}>
          {ACCENTS.map((c,i)=><button key={i} onClick={()=>set("ai",i)} style={{width:28,height:28,borderRadius:"50%",background:c,border:`3px solid ${f.ai===i?"#20264B":"transparent"}`,cursor:"pointer",boxShadow:f.ai===i?`0 0 0 2px ${c}`:"none"}}/>)}
        </div>
        <div style={{display:"grid",gap:11}}>
          <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>NOME DO ESPAÇO *</p><input className="inp" placeholder="Ex: Gran Luce" value={f.name} onChange={e=>set("name",e.target.value)}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>TIPO</p><select className="inp" value={f.type} onChange={e=>set("type",e.target.value)}>{TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
            <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>SÍNDICO</p><input className="inp" placeholder="Nome completo" value={f.manager} onChange={e=>set("manager",e.target.value)}/></div>
          </div>
          <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>ENDEREÇO</p><input className="inp" placeholder="Rua, número – Cidade/UF" value={f.address} onChange={e=>set("address",e.target.value)}/></div>
          <div style={{borderTop:"1px solid #ECEDF3",paddingTop:14}}>
            <p style={{fontSize:12,color:"#EC4899",fontWeight:600,marginBottom:12}}>🔐 Acesso do Síndico</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
              <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>E-MAIL *</p><input className="inp" type="email" placeholder="sindico@email.com" value={f.email} onChange={e=>set("email",e.target.value)}/></div>
              <div><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>SENHA *</p><input className="inp" type="text" placeholder="Definir senha" value={f.pass} onChange={e=>set("pass",e.target.value)}/></div>
            </div>
            <div style={{marginTop:11}}><p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>WHATSAPP (DDI+DDD+número)</p><input className="inp" type="tel" placeholder="5519999990000" value={f.whats} onChange={e=>set("whats",e.target.value.replace(/[^0-9]/g,""))}/><p style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>📲 Síndico recebe notificação automática quando chamado é escalado</p></div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button className="gbtn" onClick={onClose} style={{flex:1,justifyContent:"center"}}>Cancelar</button>
          <button className="mbtn" onClick={submit} disabled={!ok||loading} style={{flex:2,justifyContent:"center"}}>
            {loading?<><span className="spin-i"/>Criando…</>:"✨ Criar Condomínio"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   PORTAL DO MORADOR
════════════════════════════════════ */
function MoradorPortal({condo, tickets, setTickets, addToast, syncTicket}) {
  const [step,        setStep]        = useState("home");
  const [tid,         setTid]         = useState(null);
  const [inp,         setInp]         = useState("");
  const [busy,        setBusy]        = useState(false);
  const [resident,    setResident]    = useState(null);
  const [idForm,      setIdForm]      = useState({name:"",unit:"",phone:""});
  const [idErr,       setIdErr]       = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const botRef  = useRef(null);
  const fileRef = useRef(null);
  const notifiedRef = useRef(new Set()); // chamados que JÁ dispararam aviso (trava anti-duplicata)
  const a = getAcc(condo);

  useEffect(()=>{ botRef.current?.scrollIntoView({behavior:"smooth"}); },[step, tid, busy]);

  const tk = tickets.find(t=>t.id===tid);
  const myTickets = resident ? tickets.filter(t=>t.condoId===condo.id && t.resident?.phone===resident.phone) : [];

  // Persiste o ticket ativo no banco sempre que ele muda (criar, msg, resposta da IA, status)
  useEffect(() => { if (tk) syncTicket(tk); }, [tk, syncTicket]);

  const aiReply = useCallback(async (id, msgs) => {
    setBusy(true);
    try {
      const raw = await askClaude(msgs, condo.faq, condo.name, resident?.name);
      const shouldEsc     = raw.includes("[ESCALAR]");
      const shouldResolve = raw.includes("[RESOLVIDO]");
      const text = raw.replace("[ESCALAR]","").replace("[RESOLVIDO]","").trim();
      const newStatus = shouldResolve ? "resolved" : shouldEsc ? "in-progress" : null;

      setTickets(p => p.map(t => {
        if (t.id !== id) return t;
        return {
          ...t,
          msgs:[...t.msgs, {by:"ai",text,ts:Date.now()}],
          esc: shouldEsc || t.esc,
          status: newStatus || t.status,
        };
      }));

      if (shouldResolve) addToast("✅ Chamado encerrado automaticamente!", "success");
      const curTk = tickets.find(t=>t.id===id) || {};
      if (shouldEsc && !notifiedRef.current.has(id)) {
        notifiedRef.current.add(id); // marca ANTES de enviar — garante envio único por chamado
        const fullMsgs = [...msgs, {by:"ai", text, ts:Date.now()}];
        fetch("/api/notify", {method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({condoToken:condo.token, category:curTk.cat, ticketCode:id, resident, summary:buildSummary(fullMsgs)})}).catch(()=>{});
        addToast("Responsáveis notificados", "warning", 6000);
      }
    } catch {
      setTickets(p => p.map(t => t.id!==id?t:{...t, msgs:[...t.msgs,{by:"ai",text:"Problema técnico. Chamado escalado ao síndico.",ts:Date.now()}], esc:true, status:"in-progress"}));
      addToast("⚠️ Erro técnico — síndico será notificado","error");
    } finally { setBusy(false); }
  }, [condo, resident, tickets, setTickets, addToast]);

  const openCat = (cat) => {
    const id=uid(); const msg={by:"user",text:cat.name,ts:Date.now()};
    const t={id,condoId:condo.id,icon:cat.icon,cat:cat.name,status:"open",esc:false,rating:null,resident,createdAt:new Date().toISOString(),msgs:[msg]};
    setTickets(p=>[t,...p]); setTid(id); setStep("chat");
    setTimeout(()=>aiReply(id,[msg]),50);
  };

  const sendMsg = () => {
    const text=inp.trim(); if(!text||busy)return; setInp("");
    const msg={by:"user",text,ts:Date.now()};
    let updated;
    setTickets(p=>p.map(t=>{if(t.id!==tid)return t;updated=[...t.msgs,msg];return{...t,msgs:updated};}));
    setTimeout(()=>aiReply(tid,updated),50);
  };

  const sendFile = () => {
    if(!pendingFile||busy)return;
    const msg={by:"user",text:"",file:pendingFile,ts:Date.now()};
    let updated;
    setTickets(p=>p.map(t=>{if(t.id!==tid)return t;updated=[...t.msgs,msg];return{...t,msgs:updated};}));
    setPendingFile(null);
    const notify={by:"user",text:`📎 ${pendingFile.type==="image"?"Imagem":"Vídeo"}: ${pendingFile.name}`,ts:Date.now()+1};
    setTimeout(()=>aiReply(tid,[...updated,notify]),50);
  };

  const handleFileChange = (e) => {
    const file=e.target.files[0]; if(!file)return;
    const isVideo=file.type.startsWith("video/"), isImage=file.type.startsWith("image/");
    if(!isVideo&&!isImage)return;
    const reader=new FileReader();
    reader.onload=(ev)=>setPendingFile({dataUrl:ev.target.result,type:isVideo?"video":"image",name:file.name});
    reader.readAsDataURL(file); e.target.value="";
  };

  const startFree = () => {
    const text=inp.trim(); if(!text)return;
    const id=uid(); const msg={by:"user",text,ts:Date.now()};
    const t={id,condoId:condo.id,icon:"❓",cat:"Solicitação livre",status:"open",esc:false,rating:null,resident,createdAt:new Date().toISOString(),msgs:[msg]};
    setTickets(p=>[t,...p]); setTid(id); setInp(""); setStep("chat");
    setTimeout(()=>aiReply(id,[msg]),50);
  };

  const validateAndContinue = (goTo) => {
    if(!idForm.name.trim()||!idForm.unit.trim()||!idForm.phone.trim()){setIdErr("Preencha todos os campos.");return;}
    const phone=idForm.phone.replace(/[^0-9]/g,"");
    if(phone.length<10){setIdErr("Celular inválido — informe DDD + número.");return;}
    setResident({name:idForm.name.trim(),unit:idForm.unit.trim(),phone});
    setIdErr(""); setStep(goTo);
  };

  /* ── RENDERIZAÇÃO BASEADA EM step ── */
  const content = (() => {
    if (step==="home") return (
      <div style={{maxWidth:420,margin:"0 auto",padding:"28px 18px"}} className="fu">
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:72,height:72,borderRadius:20,background:`${a}22`,border:`2px solid ${a}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:33,margin:"0 auto 13px",color:a}}><Building2 size={34}/></div>
          <h1 style={{fontSize:21,fontWeight:800,color:"#20264B"}}>{condo.name}</h1>
          <p style={{color:"#6B7280",fontSize:14,marginTop:4}}>Escuta — Atendimento inteligente</p>
        </div>
        <div style={{background:`${a}14`,border:`1px solid ${a}33`,borderRadius:12,padding:"12px 16px",marginBottom:18,display:"flex",gap:10,alignItems:"center"}}>
          <CheckCircle2 size={20} style={{color:a,flexShrink:0}}/>
          <div><p style={{fontWeight:600,fontSize:14,color:a}}>QR Code reconhecido</p><p style={{fontSize:12,color:"#6B7280",marginTop:1}}>Como podemos ajudar hoje?</p></div>
        </div>
        {resident && (
          <div style={{background:"#FFFFFF",border:"1px solid #ECEDF3",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div><div style={{fontSize:13,fontWeight:600,color:"#20264B",display:"flex",alignItems:"center",gap:5}}><User size={13}/>{resident.name}</div><div style={{fontSize:11,color:"#6B7280",marginTop:1}}>Unid. {resident.unit}</div></div>
            <button onClick={()=>{setResident(null);setIdForm({name:"",unit:"",phone:""}); }} style={{background:"none",border:"none",color:"#9CA3AF",fontSize:11,cursor:"pointer",textDecoration:"underline"}}>Trocar</button>
          </div>
        )}
        <button onClick={()=>setStep(resident?"cat":"identify-cat")} className="mbtn" style={{width:"100%",justifyContent:"center",padding:"13px",fontSize:15,marginBottom:9,background:`linear-gradient(135deg,${a},${a}bb)`}}><Plus size={17}/>Fazer uma Solicitação</button>
        <button onClick={()=>setStep(resident?"status":"identify-status")} className="gbtn" style={{width:"100%",justifyContent:"center",padding:"11px",fontSize:14}}><Ticket size={16}/>Ver Meus Chamados</button>
      </div>
    );

    if (step==="identify-cat"||step==="identify-status") return (
      <div style={{maxWidth:420,margin:"0 auto",padding:"24px 18px"}} className="fu">
        <button onClick={()=>setStep("home")} style={{background:"none",border:"none",color:a,fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:16,padding:0}}>← Voltar</button>
        <div style={{textAlign:"center",marginBottom:22}}>
          <div style={{marginBottom:10,color:a,display:"flex",justifyContent:"center"}}><User size={38}/></div>
          <h2 style={{fontSize:18,fontWeight:800,color:"#20264B"}}>Identifique-se</h2>
          <p style={{fontSize:13,color:"#6B7280",marginTop:5,lineHeight:1.5}}>Para abrir um chamado precisamos identificar você. Seus dados ficam vinculados apenas aos seus chamados.</p>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div><label style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",display:"block",marginBottom:6}}>NOME COMPLETO *</label><input className="inp" placeholder="Ex: João da Silva" value={idForm.name} onChange={e=>{setIdForm(p=>({...p,name:e.target.value}));setIdErr("");}}/></div>
          <div><label style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",display:"block",marginBottom:6}}>UNIDADE *</label><input className="inp" placeholder="Ex: Apto 302, Torre B" value={idForm.unit} onChange={e=>{setIdForm(p=>({...p,unit:e.target.value}));setIdErr("");}}/></div>
          <div>
            <label style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",display:"block",marginBottom:6}}>CELULAR (com DDD) *</label>
            <input className="inp" placeholder="(19) 99999-9999" type="tel" value={idForm.phone}
              onChange={e=>{
                const v=e.target.value.replace(/[^0-9]/g,"").slice(0,11);
                const fmtP=v.length<=2?v:v.length<=7?`(${v.slice(0,2)}) ${v.slice(2)}`:`(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
                setIdForm(p=>({...p,phone:fmtP}));setIdErr("");
              }}/>
          </div>
        </div>
        {idErr && <div style={{background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.2)",borderRadius:8,padding:"9px 13px",fontSize:13,color:"#f87171",marginTop:12}}>⚠ {idErr}</div>}
        <button onClick={()=>validateAndContinue(step==="identify-cat"?"cat":"status")} className="mbtn" style={{width:"100%",justifyContent:"center",padding:"13px",fontSize:15,marginTop:16,background:`linear-gradient(135deg,${a},${a}bb)`}}>Continuar →</button>
        <p style={{fontSize:11,color:"#9CA3AF",textAlign:"center",marginTop:12,lineHeight:1.5}}>🔒 Dados usados apenas para identificar seus chamados neste condomínio.</p>
      </div>
    );

    if (step==="cat") return (
      <div style={{maxWidth:420,margin:"0 auto",padding:"18px"}} className="fu">
        <button onClick={()=>setStep("home")} style={{background:"none",border:"none",color:a,fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:14,padding:0}}>← Voltar</button>
        <h2 style={{fontSize:17,fontWeight:700,marginBottom:2,color:"#20264B"}}>Qual é o assunto?</h2>
        <p style={{fontSize:13,color:"#6B7280",marginBottom:14}}>Olá, <strong style={{color:"#20264B"}}>{resident?.name?.split(" ")[0]}</strong>! Selecione ou descreva livremente.</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:13}}>
          {CATS.map(c=>(
            <button key={c.id} onClick={()=>openCat(c)} style={{background:c.urgent?"#2a0f0f":"#FFFFFF",border:`1.5px solid ${c.urgent?"#7f1d1d":"#ECEDF3"}`,borderRadius:11,padding:"14px 11px",textAlign:"left",cursor:"pointer",transition:"border .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=a;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=c.urgent?"#7f1d1d":"#ECEDF3";}}>
              <div style={{marginBottom:6,color:"#EC4899"}}><EIcon e={c.icon} size={24}/></div>
              <div style={{fontSize:13,fontWeight:600,color:c.urgent?"#fca5a5":"#20264B",lineHeight:1.3}}>{c.name}</div>
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <input className="inp" style={{flex:1}} placeholder="Ou descreva sua solicitação..." value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&startFree()}/>
          <button onClick={startFree} disabled={!inp.trim()} className="mbtn" style={{padding:"9px 14px",opacity:inp.trim()?1:.45}}>→</button>
        </div>
      </div>
    );

    if (step==="chat"&&tk) return (
      <div style={{display:"flex",flexDirection:"column",height:560,maxWidth:420,margin:"0 auto"}}>
        <div style={{padding:"12px 15px",borderBottom:"1px solid #ECEDF3",display:"flex",alignItems:"center",gap:9,flexShrink:0}}>
          <button onClick={()=>setStep("cat")} style={{background:"none",border:"none",color:"#6B7280",fontSize:18,cursor:"pointer",padding:0,lineHeight:1}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14,color:"#20264B"}}><CatLabel icon={tk.icon} name={tk.cat}/></div>
            <div style={{fontSize:11,color:"#6B7280"}}>{tk.id} · Unid. {tk.resident?.unit}</div>
          </div>
          <StatusTag status={tk.status}/>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"14px",display:"flex",flexDirection:"column",gap:10}}>
          {tk.esc && tk.status!=="resolved" && <div style={{background:"rgba(245,158,11,.1)",border:"1px solid rgba(245,158,11,.22)",borderRadius:9,padding:"8px 12px",fontSize:12,color:"#f59e0b",display:"flex",alignItems:"center",gap:6}}><Bell size={13}/>Escalado para atendente. Síndico notificado.</div>}
          {tk.status==="resolved" && <div style={{background:"rgba(52,211,153,.1)",border:"1px solid rgba(52,211,153,.22)",borderRadius:9,padding:"8px 12px",fontSize:12,color:"#34d399"}}>✅ Este chamado foi encerrado.</div>}
          {tk.msgs.map((m,i)=><MsgBubble key={i} m={m} accentColor={a}/>)}
          {busy && <div style={{display:"flex",gap:8,alignItems:"center"}}><div style={{padding:"9px 14px",background:"#EEF0F6",borderRadius:"14px 14px 14px 3px"}}><div className="dot"><span/><span/><span/></div></div><span style={{fontSize:11,color:"#9CA3AF"}}>digitando…</span></div>}
          <div ref={botRef}/>
        </div>
        {tk.status!=="resolved" && (
          <div style={{borderTop:"1px solid #ECEDF3",flexShrink:0}}>
            {pendingFile && (
              <div style={{padding:"10px 14px",borderBottom:"1px solid #ECEDF3",display:"flex",alignItems:"center",gap:10,background:"#F7F8FC"}}>
                <div style={{width:48,height:48,borderRadius:8,overflow:"hidden",flexShrink:0,background:"#ECEDF3",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {pendingFile.type==="image"?<img src={pendingFile.dataUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<Film size={22} color="#6B7280"/>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:"#20264B",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{pendingFile.name}</div>
                  <div style={{fontSize:11,color:"#6B7280",marginTop:2}}>{pendingFile.type==="image"?"Imagem":"Vídeo"} — pronto para enviar</div>
                </div>
                <button onClick={()=>setPendingFile(null)} style={{background:"none",border:"none",color:"#6B7280",fontSize:18,cursor:"pointer",padding:0}}>✕</button>
                <button onClick={sendFile} className="mbtn" style={{padding:"7px 14px",fontSize:13,flexShrink:0}}>Enviar</button>
              </div>
            )}
            <div style={{padding:"11px 14px",display:"flex",gap:8,alignItems:"center"}}>
              <input ref={fileRef} type="file" accept="image/*,video/*" style={{display:"none"}} onChange={handleFileChange}/>
              <button onClick={()=>fileRef.current?.click()} disabled={busy} title="Enviar imagem ou vídeo"
                style={{width:41,height:41,borderRadius:9,background:"#ECEDF3",border:"1px solid #ECEDF3",cursor:busy?"default":"pointer",color:"#6B7280",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .2s"}}
                onMouseEnter={e=>{if(!busy)e.currentTarget.style.borderColor="#EC4899";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#ECEDF3";}}><Paperclip size={18}/></button>
              <input className="inp" placeholder={busy?"Aguarde…":"Sua mensagem..."} value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendMsg()} disabled={busy} style={{flex:1}}/>
              <button onClick={sendMsg} disabled={busy||!inp.trim()} style={{width:41,height:41,borderRadius:9,background:(!busy&&inp.trim())?a:"#ECEDF3",border:"none",cursor:inp.trim()?"pointer":"default",color:inp.trim()?"#000":"#fff",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>➤</button>
            </div>
          </div>
        )}
      </div>
    );

    /* STATUS */
    return (
      <div style={{maxWidth:420,margin:"0 auto",padding:"18px"}} className="fu">
        <button onClick={()=>setStep("home")} style={{background:"none",border:"none",color:a,fontSize:14,fontWeight:600,cursor:"pointer",marginBottom:14,padding:0}}>← Início</button>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:14}}>
          <h2 style={{fontSize:17,fontWeight:700,color:"#20264B"}}>Meus Chamados</h2>
          {resident && <span style={{fontSize:12,color:"#6B7280"}}>Unid. {resident.unit}</span>}
        </div>
        {myTickets.length===0 && (
          <div style={{textAlign:"center",padding:"32px 16px",color:"#9CA3AF"}}>
            <div style={{fontSize:40,marginBottom:12}}>📭</div>
            <p style={{fontSize:14,fontWeight:500}}>Nenhum chamado encontrado</p>
            <p style={{fontSize:12,marginTop:4}}>Seus chamados aparecem aqui após a primeira solicitação.</p>
          </div>
        )}
        {myTickets.map(t=>(
          <div key={t.id} onClick={()=>{setTid(t.id);setStep("chat");}} className="card" style={{marginBottom:9,borderLeft:`3px solid ${SM[t.status]?.color||"#f59e0b"}`,cursor:"pointer"}}
            onMouseEnter={e=>{e.currentTarget.style.background="#F1F2F8";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#FFFFFF";}}>
            <div style={{display:"flex",justifyContent:"space-between"}}><div style={{fontWeight:600,fontSize:14,color:"#20264B"}}><CatLabel icon={t.icon} name={t.cat}/></div><StatusTag status={t.status}/></div>
            <div style={{fontSize:12,color:"#9CA3AF",marginTop:4}}>{t.id} · {fmtF(t.createdAt)}</div>
            {t.msgs?.length>0 && <div style={{fontSize:12,color:"#9CA3AF",marginTop:5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.msgs[t.msgs.length-1].text||"📎 mídia"}</div>}
          </div>
        ))}
      </div>
    );
  })();

  return <div style={{fontFamily:"Plus Jakarta Sans,sans-serif",color:"#20264B",background:"#F7F8FC",minHeight:"100%"}}>{content}</div>;
}

/* ════════════════════════════════════
   PAINEL TICKETS (gestor/síndico)
════════════════════════════════════ */
function TicketPanel({tickets, setTickets, syncTicket, condoId, accent, onGoTo}) {
  const [sel,   setSel]   = useState(null);
  const [reply, setReply] = useState("");
  const [fil,   setFil]   = useState("all");
  const a = accent||"#EC4899";
  const mine  = tickets.filter(t=>t.condoId===condoId);
  const shown = fil==="all"?mine:mine.filter(t=>t.status===fil);
  const tk    = mine.find(t=>t.id===sel);

  const doReply=()=>{
    if(!reply.trim()||!sel)return;
    const cur=tickets.find(t=>t.id===sel);
    const nt={...cur,msgs:[...cur.msgs,{by:"gestor",text:reply.trim(),ts:Date.now()}],status:"resolved"};
    setTickets(p=>p.map(t=>t.id===sel?nt:t));
    if(syncTicket) syncTicket(nt);
    setReply("");
  };

  return (
    <div style={{display:"flex",gap:13,height:520}}>
      <div style={{width:250,flexShrink:0,display:"flex",flexDirection:"column",gap:7}}>
        <div style={{display:"flex",gap:4}}>
          {[["all","Todos"],["open","Aberto"],["in-progress","Em atend."],["resolved","Ok"]].map(([k,l])=>(
            <button key={k} onClick={()=>setFil(k)} style={{flex:1,padding:"5px 3px",borderRadius:7,border:"1px solid",borderColor:fil===k?a:"#ECEDF3",background:fil===k?`${a}22`:"transparent",color:fil===k?a:"#6B7280",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"Plus Jakarta Sans,sans-serif"}}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
          {shown.map(t=>(
            <div key={t.id} onClick={()=>setSel(t.id)} className="card" style={{padding:11,cursor:"pointer",borderLeft:`3px solid ${sel===t.id?a:(SM[t.status]?.color||"#f59e0b")}`,background:sel===t.id?"#F1F2F8":"#FFFFFF"}}
              onMouseEnter={e=>{if(sel!==t.id)e.currentTarget.style.background="#F1F2F8";}}
              onMouseLeave={e=>{if(sel!==t.id)e.currentTarget.style.background="#FFFFFF";}}>
              <div style={{fontWeight:600,fontSize:13,marginBottom:5,color:"#20264B"}}><CatLabel icon={t.icon} name={t.cat}/></div>
              {t.resident && <div style={{fontSize:11,color:"#6B7280",marginBottom:4}}>{t.resident.name} · Unid. {t.resident.unit}</div>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}><div style={{fontSize:11,color:"#9CA3AF"}}>{t.id}</div><StatusTag status={t.status}/></div>
            </div>
          ))}
          {shown.length===0 && <p style={{color:"#9CA3AF",fontSize:13,padding:8}}>Nenhum ticket.</p>}
        </div>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column"}} className="card">
        {!tk?(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:9,color:"#9CA3AF"}}>
            <span style={{fontSize:42}}>📋</span><p style={{fontSize:14}}>Selecione um ticket</p>
          </div>
        ):(
          <>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,paddingBottom:11,borderBottom:"1px solid #ECEDF3"}}>
              <div><div style={{fontWeight:700,fontSize:16,color:"#20264B"}}><CatLabel icon={tk.icon} name={tk.cat}/></div><div style={{fontSize:12,color:"#9CA3AF",marginTop:2}}>{tk.id} · {fmtF(tk.createdAt)}</div></div>
              <StatusTag status={tk.status}/>
            </div>
            {tk.resident && (
              <div style={{background:"#F7F8FC",border:"1px solid #ECEDF3",borderRadius:8,padding:"8px 13px",fontSize:12,color:"#6B7280",marginBottom:11,display:"flex",gap:14,flexWrap:"wrap"}}>
                <span>👤 <strong style={{color:"#20264B"}}>{tk.resident.name}</strong></span>
                <span>🏠 Unid. <strong style={{color:"#20264B"}}>{tk.resident.unit}</strong></span>
                <span>📱 {tk.resident.phone.replace(/^(\d{2})(\d{5})(\d{4})$/,"($1) $2-$3")}</span>
              </div>
            )}
            {tk.esc && tk.status!=="resolved" && <div style={{background:"rgba(245,158,11,.1)",border:"1px solid rgba(245,158,11,.2)",borderRadius:8,padding:"7px 11px",fontSize:12,color:"#f59e0b",marginBottom:11,display:"flex",alignItems:"center",gap:6}}><Bell size={13}/>Escalado — aguarda resposta</div>}
            <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:9,marginBottom:11}}>
              {tk.msgs.map((m,i)=><MsgBubbleAdmin key={i} m={m}/>)}
            </div>
            {tk.status!=="resolved"?(
              <div style={{display:"flex",gap:8}}>
                <textarea className="inp" rows={2} placeholder="Responder ao morador..." value={reply} onChange={e=>setReply(e.target.value)} style={{flex:1,resize:"none",lineHeight:1.5}}/>
                <button onClick={doReply} disabled={!reply.trim()} className="mbtn" style={{alignSelf:"stretch",padding:"0 13px",flexShrink:0,opacity:reply.trim()?1:.45,flexDirection:"column",fontSize:12,gap:2}}><span>Enviar</span><span>e Fechar</span></button>
              </div>
            ):<div style={{textAlign:"center",color:"#34d399",fontSize:13,fontWeight:600,padding:8}}>✅ Ticket encerrado</div>}
          </>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   PAINEL DO SÍNDICO
════════════════════════════════════ */
function CondoPanel({condo, tickets, setTickets, syncTicket, onSimulate}) {
  const [tab,setTab]=useState("dash");
  const a=getAcc(condo);
  const mine=tickets.filter(t=>t.condoId===condo.id);
  const cnt={all:mine.length,open:mine.filter(t=>t.status==="open").length,"in-progress":mine.filter(t=>t.status==="in-progress").length,resolved:mine.filter(t=>t.status==="resolved").length};

  return (
    <div>
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:"1px solid #ECEDF3"}}>
        {[["dash",LayoutDashboard,"Dashboard"],["tickets",Ticket,"Tickets"],["qr",QrCode,"QR Code"]].map(([v,Ic,l])=>(
          <button key={v} className={`tab-btn ${tab===v?"on":""}`} onClick={()=>setTab(v)} style={{display:"inline-flex",alignItems:"center",gap:7}}><Ic size={16}/>{l}</button>
        ))}
      </div>
      {tab==="dash" && (
        <div className="fi">
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(155px,1fr))",gap:12,marginBottom:20}}>
            <StatCard icon={<Ticket size={22}/>} label="Total"      value={cnt.all}            color={a}/>
            <StatCard icon={<CircleDot size={22}/>} label="Abertos"    value={cnt.open}           color="#f59e0b" sub={cnt.open>0?"aguardando":""}/>
            <StatCard icon={<Clock size={22}/>} label="Em Atend."  value={cnt["in-progress"]} color="#38bdf8"/>
            <StatCard icon={<CheckCircle2 size={22}/>} label="Resolvidos" value={cnt.resolved}        color="#34d399"/>
          </div>
          <div className="card">
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:14}}>ATIVIDADE RECENTE</p>
            {mine.length===0 && <p style={{color:"#9CA3AF",fontSize:13}}>Nenhum ticket ainda.</p>}
            {mine.slice(0,8).map(t=>(
              <div key={t.id} className="row">
                <EIcon e={t.icon} size={19}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:500,color:"#20264B"}}>{t.cat}</div>
                  <div style={{fontSize:11,color:"#9CA3AF"}}>{t.id} · {t.resident?.name||"—"} · {fmtF(t.createdAt)}</div>
                </div>
                <StatusTag status={t.status}/>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab==="tickets" && <div className="fi"><TicketPanel tickets={tickets} setTickets={setTickets} syncTicket={syncTicket} condoId={condo.id} accent={a}/></div>}
      {tab==="qr"      && <div className="fi"><PlacaGenerator condo={condo}/><div style={{marginTop:20}}>{onSimulate && <button onClick={onSimulate} className="gbtn" style={{fontSize:13}}>📱 Simular Escaneamento</button>}</div></div>}
    </div>
  );
}

/* ════════════════════════════════════
   FAQ EDITOR INLINE
════════════════════════════════════ */
function FaqEditor({condo, onSave}) {
  const [text, setText] = useState(condo.faq || "");
  const [saved, setSaved] = useState(false);
  const handle = () => {
    onSave(text);
    setSaved(true);
    setTimeout(()=>setSaved(false), 2000);
  };
  return (
    <div>
      <textarea
        className="inp"
        rows={7}
        value={text}
        onChange={e=>{ setText(e.target.value); setSaved(false); }}
        placeholder="Ex: Academia: 6h-22h. Salão: reservar com 72h. Taxa vence dia 10..."
        style={{resize:"vertical",lineHeight:1.7,fontSize:13}}
      />
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
        <button onClick={handle} className="mbtn" style={{fontSize:13,padding:"7px 16px"}}>
          {saved ? "✓ Salvo!" : "💾 Salvar FAQ"}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   MODAL EDITAR CONDOMÍNIO
════════════════════════════════════ */
function EditCondoModal({condo, onClose, onSave}) {
  const [f, setF] = useState({
    name:       condo.name,
    type:       condo.type,
    address:    condo.address    || "",
    adminEmail: condo.adminEmail || "",
    adminName:  condo.adminName  || "",
    adminWhats: condo.adminWhats || "",
    faq:        condo.faq        || "",
    emoji:      condo.emoji      || "🏢",
    ai:         condo.ai ?? (ACCENTS.indexOf(condo.accent) >= 0 ? ACCENTS.indexOf(condo.accent) : 0),
    logo:       condo.logo       || null,
  });
  const set = (k,v) => setF(p => ({...p,[k]:v}));
  const logoRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = ev => set("logo", ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="mbg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal fu" style={{width:520}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:700,color:"#20264B"}}><Pencil size={14}/> Editar — {condo.emoji} {condo.name}</h2>
            <p style={{fontSize:13,color:"#6B7280",marginTop:3}}>Atualize as informações do condomínio</p>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B7280",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        {/* Ícone + cor */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:8}}>ÍCONE</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {EMOJIS.map(e=><button key={e} onClick={()=>set("emoji",e)} style={{width:34,height:34,borderRadius:8,border:`2px solid ${f.emoji===e?"#EC4899":"#ECEDF3"}`,background:f.emoji===e?"rgba(236,72,153,.15)":"transparent",fontSize:16,cursor:"pointer"}}>{e}</button>)}
            </div>
          </div>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:8}}>COR DE DESTAQUE</p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {ACCENTS.map((c,i)=><button key={i} onClick={()=>set("ai",i)} style={{width:28,height:28,borderRadius:"50%",background:c,border:`3px solid ${f.ai===i?"#20264B":"transparent"}`,cursor:"pointer",boxShadow:f.ai===i?`0 0 0 2px ${c}`:"none"}}/>)}
            </div>
          </div>
        </div>

        <div style={{display:"grid",gap:11}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
            <div>
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>NOME *</p>
              <input className="inp" value={f.name} onChange={e=>set("name",e.target.value)}/>
            </div>
            <div>
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>TIPO</p>
              <select className="inp" value={f.type} onChange={e=>set("type",e.target.value)}>
                {TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>ENDEREÇO</p>
            <input className="inp" placeholder="Rua, número – Cidade/UF" value={f.address} onChange={e=>set("address",e.target.value)}/>
          </div>

          <div style={{borderTop:"1px solid #ECEDF3",paddingTop:14}}>
            <p style={{fontSize:12,color:"#EC4899",fontWeight:600,marginBottom:12}}>🖼 Logo do Condomínio</p>
            <p style={{fontSize:12,color:"#6B7280",marginBottom:12,lineHeight:1.5}}>Aparece nas placas geradas e no painel. PNG ou JPG com fundo transparente ou branco recomendado.</p>
            <div style={{display:"flex",gap:14,alignItems:"center"}}>
              <div style={{width:80,height:80,borderRadius:12,border:"2px dashed #ECEDF3",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",background:"#F7F8FC",flexShrink:0}}>
                {f.logo
                  ? <img src={f.logo} style={{width:"100%",height:"100%",objectFit:"contain"}} alt="logo"/>
                  : <span style={{fontSize:28,opacity:.4}}>🖼</span>
                }
              </div>
              <div style={{flex:1}}>
                <input ref={logoRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleLogoUpload}/>
                <button onClick={()=>logoRef.current?.click()} className="gbtn" style={{width:"100%",justifyContent:"center",marginBottom:6}}>
                  {f.logo ? "🔄 Trocar Logo" : "⬆ Fazer Upload do Logo"}
                </button>
                {f.logo && (
                  <button onClick={()=>set("logo",null)} style={{width:"100%",background:"none",border:"none",color:"#ef4444",fontSize:12,cursor:"pointer",fontFamily:"Plus Jakarta Sans,sans-serif"}}>
                    × Remover logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div style={{borderTop:"1px solid #ECEDF3",paddingTop:14}}>
            <p style={{fontSize:12,color:"#EC4899",fontWeight:600,marginBottom:12,display:"flex",alignItems:"center",gap:6}}><User size={14}/>Dados do Síndico</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:11}}>
              <div>
                <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>NOME DO SÍNDICO</p>
                <input className="inp" placeholder="Nome completo" value={f.adminName} onChange={e=>set("adminName",e.target.value)}/>
              </div>
              <div>
                <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>E-MAIL</p>
                <input className="inp" type="email" value={f.adminEmail} onChange={e=>set("adminEmail",e.target.value)}/>
              </div>
            </div>
            <div style={{marginTop:11}}>
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>WHATSAPP (DDI+DDD+número)</p>
              <input className="inp" type="tel" placeholder="5519999990000" value={f.adminWhats} onChange={e=>set("adminWhats",e.target.value.replace(/[^0-9]/g,""))}/>
            </div>
          </div>

          <div style={{borderTop:"1px solid #ECEDF3",paddingTop:14}}>
            <p style={{fontSize:12,color:"#EC4899",fontWeight:600,marginBottom:6}}>📋 FAQ / Base de Conhecimento da IA</p>
            <p style={{fontSize:12,color:"#6B7280",marginBottom:8,lineHeight:1.5}}>Estas informações são usadas pela IA para responder os moradores. Inclua horários, regras, normas e contatos importantes.</p>
            <textarea className="inp" rows={6} value={f.faq} onChange={e=>set("faq",e.target.value)} placeholder="Ex: Academia: 6h-22h. Salão: reservar com 72h. Piscina: ter-dom 8h-20h..." style={{resize:"vertical",lineHeight:1.6}}/>
          </div>
        </div>

        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button className="gbtn" onClick={onClose} style={{flex:1,justifyContent:"center"}}>Cancelar</button>
          <button className="mbtn" onClick={()=>onSave({...condo,...f})} disabled={!f.name.trim()} style={{flex:2,justifyContent:"center"}}>
            💾 Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   MODAL EDITAR USUÁRIO
════════════════════════════════════ */
function EditUserModal({user, condos, onClose, onSave}) {
  const [f, setF] = useState({
    name:    user.name    || "",
    email:   user.email   || "",
    pass:    user.pass    || "",
    condoId: user.condoId || "",
  });
  const set = (k,v) => setF(p=>({...p,[k]:v}));
  const ok = f.name.trim() && f.email.trim();

  return (
    <div className="mbg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal fu" style={{width:440}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:700,color:"#20264B"}}><Pencil size={14}/> Editar Usuário</h2>
            <p style={{fontSize:13,color:"#6B7280",marginTop:3}}>{user.role==="super"?"Super Admin":"Síndico"} — {user.email}</p>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#6B7280",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        <div style={{display:"grid",gap:11}}>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>NOME COMPLETO *</p>
            <input className="inp" value={f.name} onChange={e=>set("name",e.target.value)}/>
          </div>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>E-MAIL DE LOGIN *</p>
            <input className="inp" type="email" value={f.email} onChange={e=>set("email",e.target.value)}/>
          </div>
          <div>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>SENHA *</p>
            <div style={{position:"relative"}}>
              <input className="inp" value={f.pass} onChange={e=>set("pass",e.target.value)} style={{fontFamily:"monospace"}}/>
            </div>
            <p style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>A senha deve ser comunicada ao usuário após a alteração.</p>
          </div>
          {user.role === "sindico" && (
            <div>
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>CONDOMÍNIO VINCULADO</p>
              <select className="inp" value={f.condoId} onChange={e=>set("condoId",e.target.value)}>
                <option value="">— Nenhum —</option>
                {condos.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button className="gbtn" onClick={onClose} style={{flex:1,justifyContent:"center"}}>Cancelar</button>
          <button className="mbtn" onClick={()=>onSave({...user,...f})} disabled={!ok} style={{flex:2,justifyContent:"center"}}>
            💾 Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   SUPER ADMIN
════════════════════════════════════ */
/* ════════════════════════════════════
   RESPONSÁVEIS (roteamento de avisos)
════════════════════════════════════ */
function ResponsaveisPanel({condo}) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({name:"",email:"",whatsapp:"",channels:["whatsapp"],categories:["all"]});
  const [loading, setLoading] = useState(false);
  const a = getAcc(condo);

  const load = useCallback(() => {
    fetch(`/api/condos/${condo.token}/responsaveis`).then(r=>r.json())
      .then(d=>{ if(Array.isArray(d)) setList(d); }).catch(()=>{});
  }, [condo.token]);
  useEffect(()=>{ load(); }, [load]);

  const toggleChannel = (ch) => setForm(f=>({...f, channels: f.channels.includes(ch)?f.channels.filter(c=>c!==ch):[...f.channels,ch]}));
  const toggleCat = (cat) => setForm(f=>{
    if (cat==="all") return {...f, categories:["all"]};
    const base = f.categories.filter(c=>c!=="all");
    const next = base.includes(cat)?base.filter(c=>c!==cat):[...base,cat];
    return {...f, categories: next.length?next:["all"]};
  });
  const add = async () => {
    if (!form.name.trim() || (!form.email.trim() && !form.whatsapp.trim())) return;
    setLoading(true);
    await fetch(`/api/condos/${condo.token}/responsaveis`, {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(form)}).catch(()=>{});
    setForm({name:"",email:"",whatsapp:"",channels:["whatsapp"],categories:["all"]});
    await load(); setLoading(false);
  };
  const remove = async (id) => { await fetch(`/api/responsaveis/${id}`,{method:"DELETE"}).catch(()=>{}); load(); };

  const chip = (on) => ({padding:"5px 11px",borderRadius:999,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${on?a:"#ECEDF3"}`,background:on?`${a}14`:"#fff",color:on?a:"#6B7280",fontFamily:"Plus Jakarta Sans,sans-serif",transition:"all .15s"});

  return (
    <div className="card" style={{marginTop:13}}>
      <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:4}}>RESPONSÁVEIS POR AVISOS</p>
      <p style={{fontSize:12.5,color:"#6B7280",marginBottom:15,lineHeight:1.5}}>Quem é avisado quando um chamado é escalado — cada um recebe só as categorias escolhidas, no canal escolhido.</p>

      {list.length===0 && <div style={{fontSize:13,color:"#9CA3AF",marginBottom:14}}>Nenhum responsável cadastrado ainda.</div>}
      {list.map(r=>(
        <div key={r.id} style={{border:"1px solid #ECEDF3",borderRadius:12,padding:"11px 13px",marginBottom:9,display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:14,fontWeight:700,color:"#20264B"}}>{r.name}</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:12,color:"#6B7280",marginTop:3}}>
              {r.whatsapp && <span style={{display:"inline-flex",alignItems:"center",gap:4}}><Smartphone size={12}/>{r.whatsapp}</span>}
              {r.email && <span style={{display:"inline-flex",alignItems:"center",gap:4}}><Mail size={12}/>{r.email}</span>}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:8}}>
              {(r.categories||[]).map(c=>(
                <span key={c} style={{fontSize:10.5,fontWeight:600,padding:"3px 8px",borderRadius:6,background:`${a}14`,color:a}}>{c==="all"?"Todas as categorias":c}</span>
              ))}
            </div>
          </div>
          <button onClick={()=>remove(r.id)} className="rbtn" style={{padding:"5px 9px",fontSize:12,flexShrink:0}}><Trash2 size={13}/></button>
        </div>
      ))}

      <div style={{borderTop:"1px solid #ECEDF3",marginTop:8,paddingTop:15}}>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:9}}>NOVO RESPONSÁVEL</p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:9}}>
          <input className="inp" placeholder="Nome" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
          <input className="inp" placeholder="WhatsApp (5519999990000)" value={form.whatsapp} onChange={e=>setForm(f=>({...f,whatsapp:e.target.value.replace(/[^0-9]/g,"")}))}/>
        </div>
        <input className="inp" placeholder="E-mail" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={{marginBottom:11}}/>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,marginBottom:7}}>AVISAR POR</p>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {[["whatsapp","WhatsApp"],["email","E-mail"]].map(([k,l])=>(
            <button key={k} onClick={()=>toggleChannel(k)} style={chip(form.channels.includes(k))}>{l}</button>
          ))}
        </div>
        <p style={{fontSize:11,color:"#6B7280",fontWeight:600,marginBottom:7}}>RECEBE QUAIS CATEGORIAS</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
          <button onClick={()=>toggleCat("all")} style={chip(form.categories.includes("all"))}>Todas</button>
          {CATS.map(c=>(
            <button key={c.id} onClick={()=>toggleCat(c.name)} style={chip(form.categories.includes(c.name))}>{c.name}</button>
          ))}
        </div>
        <button className="mbtn" onClick={add} disabled={loading||!form.name.trim()||(!form.email.trim()&&!form.whatsapp.trim())} style={{width:"100%",justifyContent:"center"}}>
          {loading?<><span className="spin-i"/>Salvando…</>:<><Plus size={16}/>Adicionar responsável</>}
        </button>
      </div>
    </div>
  );
}

function SuperAdmin({condos,setCondos,users,setUsers,tickets,setTickets,onNavCondo,reloadCondos,reloadUsers}) {
  const [tab,       setTab]       = useState("condos");
  const [create,    setCreate]    = useState(false);
  const [detId,     setDetId]     = useState(null);
  const [confirmDel,setConfirmDel]= useState(false);
  const [editCondo, setEditCondo] = useState(null);   // condo obj sendo editado
  const [editUser,  setEditUser]  = useState(null);   // user obj sendo editado
  const [editFaqId, setEditFaqId] = useState(null);   // id do condo com FAQ aberto
  const det=condos.find(c=>c.id===detId);
  const totalOpen=tickets.filter(t=>t.status==="open").length;

  const handleCreate=async(condo)=>{
    const {_newUser,...cd}=condo;
    const accent=ACCENTS[cd.ai]||"#EC4899";
    await fetch("/api/condos",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({name:cd.name,token:cd.token,type:cd.type,address:cd.address,faq:cd.faq,accent,emoji:cd.emoji,adminName:cd.adminName,adminEmail:cd.adminEmail,adminWhats:cd.adminWhats})}).catch(()=>{});
    await reloadCondos();
    if(_newUser){
      await fetch("/api/users",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({name:_newUser.name,email:_newUser.email,password:_newUser.pass,role:"sindico",condoId:cd.token})}).catch(()=>{});
      await reloadUsers();
    }
    setDetId(cd.token);setTab("condos");
  };
  const handleDelete=async(id)=>{
    const condo=condos.find(c=>c.id===id);
    if(condo?.dbId) await fetch(`/api/condos/${condo.dbId}`,{method:"DELETE"}).catch(()=>{});
    await reloadCondos();
    setTickets(p=>p.filter(t=>t.condoId!==id));
    await reloadUsers();
    setDetId(null);setConfirmDel(false);
  };
  const handleSaveCondo = async (updated) => {
    const accent=updated.accent||ACCENTS[updated.ai]||"#EC4899";
    if(editCondo?.dbId) await fetch(`/api/condos/${editCondo.dbId}`,{method:"PATCH",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({name:updated.name,type:updated.type,address:updated.address,faq:updated.faq,accent,emoji:updated.emoji,adminName:updated.adminName,adminEmail:updated.adminEmail,adminWhats:updated.adminWhats,logo:updated.logo})}).catch(()=>{});
    await reloadCondos();
    setEditCondo(null);
  };
  const handleSaveUser = (updated) => {
    setUsers(p=>p.map(u=>u.email===updated.email&&u.condoId===updated.condoId?updated:u));
    setEditUser(null);
  };
  // helper para salvar user sem fechar modal antes
  const saveUser = async (orig, updated) => {
    if(orig?.id) await fetch(`/api/users/${orig.id}`,{method:"PATCH",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({name:updated.name,email:updated.email,password:updated.pass||undefined,condoId:updated.condoId})}).catch(()=>{});
    await reloadUsers();
    setEditUser(null);
  };

  return (
    <div>
      {create && <CreateModal onClose={()=>setCreate(false)} onCreate={handleCreate}/>}
      {editCondo && <EditCondoModal condo={editCondo} onClose={()=>setEditCondo(null)} onSave={handleSaveCondo}/>}
      {editUser  && <EditUserModal  user={editUser}  condos={condos} onClose={()=>setEditUser(null)}  onSave={(u)=>saveUser(editUser,u)}/>}

      {confirmDel&&det && (
        <div className="mbg" onClick={()=>setConfirmDel(false)}>
          <div className="modal fu" style={{maxWidth:360}}>
            <div style={{textAlign:"center",marginBottom:18}}>
              <div style={{fontSize:44,marginBottom:10}}>🗑</div>
              <h2 style={{fontSize:18,fontWeight:700,color:"#20264B",marginBottom:8}}>Excluir {det.emoji} {det.name}?</h2>
              <p style={{fontSize:14,color:"#6B7280",lineHeight:1.6}}>Esta ação é <strong style={{color:"#f87171"}}>irreversível</strong>. Todos os tickets e acessos serão removidos.</p>
            </div>
            <div style={{background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.2)",borderRadius:10,padding:"10px 14px",fontSize:13,color:"#fca5a5",marginBottom:18}}>
              ⚠ {tickets.filter(t=>t.condoId===det.id).length} ticket(s) e {users.filter(u=>u.condoId===det.id).length} usuário(s) serão excluídos.
            </div>
            <div style={{display:"flex",gap:10}}>
              <button className="gbtn" onClick={()=>setConfirmDel(false)} style={{flex:1,justifyContent:"center"}}>Cancelar</button>
              <button onClick={()=>handleDelete(det.id)} style={{flex:1,background:"#dc2626",color:"#fff",border:"none",borderRadius:9,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"Plus Jakarta Sans,sans-serif"}}>Sim, excluir</button>
            </div>
          </div>
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,borderBottom:"1px solid #ECEDF3",paddingBottom:12}}>
        <div style={{display:"flex",gap:2}}>
          {[["condos",Building2,"Condomínios"],["overview",BarChart3,"Visão Geral"],["acessos",Lock,"Acessos"],["manutencao",Settings,"Manutenção"]].map(([v,Ic,l])=>(
            <button key={v} className={`tab-btn ${tab===v?"on":""}`} onClick={()=>{setTab(v);setDetId(null);}} style={{display:"inline-flex",alignItems:"center",gap:7}}><Ic size={16}/>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:9}}>
          {detId && <button className="gbtn" onClick={()=>setDetId(null)}>← Lista</button>}
          {tab==="condos"&&!detId && <button className="mbtn" onClick={()=>setCreate(true)}>+ Novo Condomínio</button>}
        </div>
      </div>

      {tab==="overview" && (
        <div className="fi">
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:12,marginBottom:20}}>
            <StatCard icon={<Building2 size={22}/>} label="Condomínios"    value={condos.length}  color="#EC4899" sub="ativos"/>
            <StatCard icon={<Ticket size={22}/>} label="Tickets Totais" value={tickets.length} color="#7c3aed"/>
            <StatCard icon={<CircleDot size={22}/>} label="Em Aberto"      value={totalOpen}      color="#f59e0b" sub={totalOpen>0?"requer atenção":"tudo em dia"}/>
            <StatCard icon={<TrendingUp size={22}/>} label="Resolvidos"     value={tickets.length?Math.round(tickets.filter(t=>t.status==="resolved").length/tickets.length*100)+"%":"—"} color="#34d399"/>
          </div>
          <div className="card">
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:14}}>TODOS OS TICKETS</p>
            {[...tickets].sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).map(t=>{
              const c=condos.find(x=>x.id===t.condoId);
              return (
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:11,padding:"10px 0",borderBottom:"1px solid #EEF0F6"}}>
                  <EIcon e={t.icon} size={19}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:500,color:"#20264B",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.cat}</div>
                    <div style={{fontSize:11,color:"#9CA3AF"}}>{t.id} · {c?`${c.emoji} ${c.name}`:"—"} · {fmtF(t.createdAt)}</div>
                  </div>
                  <StatusTag status={t.status}/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab==="acessos" && (
        <div className="fi">
          <div className="card" style={{marginBottom:14}}>
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:16}}>USUÁRIOS COM ACESSO</p>
            {users.map((u,i)=>{
              const c=condos.find(x=>x.id===u.condoId);
              const isSup=u.role==="super";
              return (
                <div key={i} style={{display:"flex",alignItems:"center",gap:13,padding:"12px 0",borderBottom:"1px solid #EEF0F6"}}>
                  <div style={{width:38,height:38,borderRadius:"50%",background:isSup?"linear-gradient(135deg,#EC4899,#DB2777)":"rgba(56,189,248,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:isSup?"#000":"#38bdf8",flexShrink:0,fontWeight:700}}>{u.name[0].toUpperCase()}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:600,color:"#20264B"}}>{u.name}</div>
                    <div style={{fontSize:12,color:"#6B7280",marginTop:1}}>{u.email}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <span style={{fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:20,background:isSup?"rgba(236,72,153,.15)":"rgba(56,189,248,.12)",color:isSup?"#EC4899":"#38bdf8"}}>
                      {isSup?"Super Admin":"Síndico"}
                    </span>
                    {c && <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{c.name}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{background:"rgba(236,72,153,.07)",border:"1px solid rgba(236,72,153,.18)",borderRadius:12,padding:"12px 16px",fontSize:13,color:"#EC4899"}}>
            💡 Novos acessos são criados ao cadastrar um condomínio. O síndico recebe e-mail e senha definidos no formulário.
          </div>
        </div>
      )}

      {tab==="condos"&&!detId && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(245px,1fr))",gap:13}} className="fi">
          {condos.map(c=>{
            const ct=tickets.filter(t=>t.condoId===c.id);
            const co=ct.filter(t=>t.status==="open").length;
            const a=getAcc(c);
            return (
              <div key={c.id} className="cc" onClick={()=>setDetId(c.id)}>
                <div style={{height:5,background:`linear-gradient(90deg,${a},${a}66)`}}/>
                <div style={{padding:"15px 17px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:11}}>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <CondoAvatar condo={c} size={40} icon={20}/>
                      <div><div style={{fontWeight:700,fontSize:15,color:"#20264B"}}>{c.name}</div><div style={{fontSize:11,color:"#6B7280"}}>{c.type}</div></div>
                    </div>
                    {co>0 && <span style={{background:"rgba(239,68,68,.2)",color:"#f87171",borderRadius:"50%",width:21,height:21,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>{co}</span>}
                  </div>
                  <p style={{fontSize:12,color:"#9CA3AF",marginBottom:9,display:"flex",alignItems:"center",gap:5}}><MapPin size={13}/>{c.address||"Sem endereço"}</p>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#6B7280",paddingTop:9,borderTop:"1px solid #EEF0F6"}}>
                    <span>{ct.length} tickets</span><span style={{color:a,fontWeight:600}}>Ver detalhes →</span>
                  </div>
                </div>
              </div>
            );
          })}
          <div onClick={()=>setCreate(true)} style={{border:"2px dashed #ECEDF3",borderRadius:14,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:9,padding:26,cursor:"pointer",transition:"all .2s",minHeight:150}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="#EC4899";e.currentTarget.style.background="rgba(236,72,153,.05)";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="#ECEDF3";e.currentTarget.style.background="transparent";}}>
            <div style={{width:42,height:42,borderRadius:11,background:"rgba(236,72,153,.1)",border:"1.5px solid rgba(236,72,153,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,color:"#EC4899"}}>+</div>
            <div style={{fontWeight:600,fontSize:14,color:"#6B7280"}}>Novo Condomínio</div>
          </div>
        </div>
      )}

      {tab==="condos"&&detId&&det && (
        <div className="fi">
          <div style={{display:"flex",alignItems:"center",gap:13,marginBottom:20}}>
            <div style={{width:52,height:52,borderRadius:14,background:`${getAcc(det)}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,overflow:"hidden",flexShrink:0}}>
              {det.logo
                ? <img src={det.logo} style={{width:"100%",height:"100%",objectFit:"contain"}} alt="logo"/>
                : det.emoji
              }
            </div>
            <div style={{flex:1}}>
              <h2 style={{fontSize:21,fontWeight:800,color:"#20264B"}}>{det.name}</h2>
              <div style={{fontSize:13,color:"#6B7280"}}>{det.type} · Desde {fmtD(det.createdAt)}</div>
            </div>
            <button onClick={()=>setConfirmDel(true)} className="rbtn">🗑 Excluir</button>
            <button onClick={()=>setEditCondo(det)} className="gbtn"><Pencil size={14}/> Editar</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:13,marginBottom:20}}>
            <div className="card">
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:9}}>ACESSO DO SÍNDICO</p>
              {(()=>{const u=users.find(x=>x.condoId===det.id);return u?(
                <>
                  <div style={{fontSize:15,fontWeight:600,color:"#20264B",marginBottom:3}}>{u.name}</div>
                  <div style={{fontSize:13,color:"#6B7280",marginBottom:3}}>{u.email}</div>
                  {det.adminWhats && <div style={{fontSize:13,color:"#6B7280",marginBottom:3}}>📱 +{det.adminWhats}</div>}
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:2}}>
                    <button onClick={()=>onNavCondo(det.id)} className="mbtn" style={{fontSize:12,padding:"7px 13px"}}>Abrir como Gestor →</button>
                    <button onClick={async()=>{
                      const r=await fetch(`/api/users/${u.id}/invite`,{method:"POST"}).then(x=>x.json()).catch(()=>null);
                      if(r?.token){ try{await navigator.clipboard.writeText(`${window.location.origin}/convite/${r.token}`);}catch{} alert("Link de convite copiado! Envie ao síndico — ele abre e cria a própria senha."); }
                      else alert("Não foi possível gerar o link.");
                    }} className="gbtn" style={{fontSize:12,padding:"7px 13px"}}>Copiar link de acesso</button>
                  </div>
                </>
              ):<p style={{color:"#9CA3AF",fontSize:13}}>Sem acesso cadastrado.</p>;})()}
            </div>
            <div className="card">
              <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:9}}>INFO</p>
              <div style={{fontSize:13,color:"#6B7280",lineHeight:1.7,marginBottom:11}}>{det.address||"Endereço não informado"}</div>
              <div style={{paddingTop:11,borderTop:"1px solid #ECEDF3"}}>
                <div style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:4}}>TICKETS</div>
                <div style={{fontSize:24,fontWeight:800,color:"#20264B"}}>{tickets.filter(t=>t.condoId===det.id).length}</div>
              </div>
            </div>
          </div>
          <div className="card">
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:16}}>QR CODE E PLACA FÍSICA</p>
            <QRSection condo={det} onSimulate={()=>onNavCondo(det.id,"morador")}/>
          </div>
          <ResponsaveisPanel condo={det}/>

      {tab==="manutencao" && (
        <div className="fi">
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <h3 style={{fontSize:15,fontWeight:700,color:"#20264B",display:"flex",alignItems:"center",gap:8}}><Building2 size={18} color="#EC4899"/>Condomínios</h3>
                <p style={{fontSize:12,color:"#6B7280",marginTop:2}}>Edite nome, endereço, cor, síndico e FAQ</p>
              </div>
              <button className="mbtn" onClick={()=>setCreate(true)} style={{fontSize:13,padding:"7px 14px"}}>+ Novo</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {condos.map(c=>{
                const ca=getAcc(c);
                const sindico=users.find(u=>u.condoId===c.id);
                const faqOpen=editFaqId===c.id;
                return (
                  <div key={c.id} className="card" style={{padding:0,overflow:"hidden"}}>
                    <div style={{height:4,background:`linear-gradient(90deg,${ca},${ca}55)`}}/>
                    <div style={{padding:"14px 18px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:42,height:42,borderRadius:11,background:`${ca}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,color:ca}}><Building2 size={21}/></div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:15,color:"#20264B"}}>{c.name}</div>
                          <div style={{fontSize:12,color:"#6B7280",marginTop:1}}>{c.type} · {c.address||"Sem endereço"}</div>
                          {sindico && <div style={{fontSize:11,color:"#9CA3AF",marginTop:2}}>👤 {sindico.name} · {sindico.email}</div>}
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>setEditFaqId(faqOpen?null:c.id)} className="gbtn" style={{fontSize:12,padding:"6px 12px"}}>{faqOpen?"▲ Fechar":"📋 FAQ"}</button>
                          <button onClick={()=>setEditCondo(c)} className="gbtn" style={{fontSize:12,padding:"6px 12px"}}><Pencil size={14}/> Editar</button>
                        </div>
                      </div>
                      {faqOpen && (
                        <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid #ECEDF3"}} className="fu">
                          <p style={{fontSize:11,color:"#EC4899",fontWeight:600,letterSpacing:"1px",marginBottom:6}}>BASE DE CONHECIMENTO DA IA</p>
                          <p style={{fontSize:12,color:"#6B7280",marginBottom:8,lineHeight:1.5}}>A IA usa este texto para responder os moradores. Inclua horários, regras e contatos.</p>
                          <FaqEditor condo={c} onSave={async(faq)=>{ if(c.dbId) await fetch(`/api/condos/${c.dbId}`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({faq})}).catch(()=>{}); await reloadCondos(); setEditFaqId(null); }}/>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div style={{marginBottom:14}}>
              <h3 style={{fontSize:15,fontWeight:700,color:"#20264B",display:"flex",alignItems:"center",gap:8}}><Lock size={17} color="#EC4899"/>Usuários e Acessos</h3>
              <p style={{fontSize:12,color:"#6B7280",marginTop:2}}>Edite nome, e-mail, senha e vínculo</p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {users.map((u,i)=>{
                const uc=condos.find(x=>x.id===u.condoId);
                const isSup=u.role==="super";
                return (
                  <div key={i} className="card" style={{display:"flex",alignItems:"center",gap:13}}>
                    <div style={{width:40,height:40,borderRadius:"50%",background:isSup?"linear-gradient(135deg,#EC4899,#DB2777)":"rgba(56,189,248,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,color:isSup?"#000":"#38bdf8",flexShrink:0,fontWeight:700}}>
                      {u.name[0].toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"#20264B"}}>{u.name}</div>
                      <div style={{fontSize:12,color:"#6B7280",marginTop:1}}>{u.email}</div>
                      <div style={{display:"flex",gap:10,marginTop:3}}>
                        <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:20,background:isSup?"rgba(236,72,153,.15)":"rgba(56,189,248,.12)",color:isSup?"#EC4899":"#38bdf8"}}>
                          {isSup?"Super Admin":"Síndico"}
                        </span>
                        {uc && <span style={{fontSize:11,color:"#9CA3AF"}}>{uc.emoji} {uc.name}</span>}
                      </div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <button onClick={()=>setEditUser(u)} className="gbtn" style={{fontSize:12,padding:"5px 12px"}}><Pencil size={14}/> Editar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════
   SIDEBAR
════════════════════════════════════ */
function SidebarContent({session,view,navigate,condos,onLogout,onClose}) {
  const isSuper=session.role==="super";
  const myCondo=condos.find(c=>c.id===session.condoId);
  const go = (v) => { navigate(v); onClose&&onClose(); };
  return (
    <>
      <div style={{padding:"15px 14px",borderBottom:"1px solid #ECEDF3",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <LogoSVG width={155} bgColor="#FFFFFF"/>
        {onClose && <button onClick={onClose} style={{background:"none",border:"none",color:"#6B7280",fontSize:22,cursor:"pointer",padding:0,lineHeight:1}}>×</button>}
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"10px 8px"}}>
        {isSuper ? (
          <>
            <p style={{fontSize:9,color:"#9CA3AF",fontWeight:700,letterSpacing:"2px",padding:"6px 9px 7px"}}>ADMIN</p>
            <div className={`nav-item ${view==="super"?"on":""}`} onClick={()=>go("super")}><Shield size={17}/><span>Painel Central</span></div>
            <p style={{fontSize:9,color:"#9CA3AF",fontWeight:700,letterSpacing:"2px",padding:"13px 9px 7px"}}>CONDOMÍNIOS</p>
            {condos.map(c=>{
              const a=getAcc(c);
              return (
                <div key={c.id} className={`nav-item ${view===`condo-${c.id}`?"on":""}`} onClick={()=>go(`condo-${c.id}`)}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:a,flexShrink:0}}/>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>
                </div>
              );
            })}
            <p style={{fontSize:9,color:"#9CA3AF",fontWeight:700,letterSpacing:"2px",padding:"13px 9px 7px"}}>SIMULAÇÃO</p>
            {condos.map(c=>(
              <div key={c.id} className={`nav-item ${view===`morador-${c.id}`?"on":""}`} onClick={()=>go(`morador-${c.id}`)}>
                <Smartphone size={15}/>
                <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12}}>Morador — {c.name}</span>
              </div>
            ))}
          </>
        ) : (
          <>
            <p style={{fontSize:9,color:"#9CA3AF",fontWeight:700,letterSpacing:"2px",padding:"6px 9px 7px"}}>MEU CONDOMÍNIO</p>
            {myCondo && (
              <div style={{padding:"10px 10px",borderRadius:10,background:`${getAcc(myCondo)}12`,border:`1px solid ${getAcc(myCondo)}22`,marginBottom:6}}>
                <div style={{marginBottom:6,color:getAcc(myCondo)}}><Building2 size={20}/></div>
                <div style={{fontSize:13,fontWeight:700,color:"#20264B"}}>{myCondo.name}</div>
                <div style={{fontSize:11,color:"#6B7280",marginTop:1}}>{myCondo.type}</div>
              </div>
            )}
            <div className={`nav-item ${view==="s-dash"?"on":""}`} onClick={()=>go("s-dash")}><LayoutDashboard size={17}/><span>Dashboard</span></div>
            <div className={`nav-item ${view==="s-tickets"?"on":""}`} onClick={()=>go("s-tickets")}><Ticket size={17}/><span>Tickets</span></div>
            <div className={`nav-item ${view==="s-qr"?"on":""}`} onClick={()=>go("s-qr")}><QrCode size={17}/><span>QR Code</span></div>
          </>
        )}
      </div>
      <div style={{padding:"10px 14px",borderTop:"1px solid #ECEDF3",fontSize:10,color:"#9CA3AF",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div><div style={{fontWeight:600,color:"#9CA3AF",marginBottom:2}}>Helgab IT</div><div>Escuta OS v1.0</div></div>
        <button className="rbtn" onClick={onLogout} style={{fontSize:11,padding:"4px 10px"}}>Sair</button>
      </div>
    </>
  );
}

function Sidebar({session,view,setView,condos}) {
  return (
    <div style={{width:210,background:"#FFFFFF",borderRight:"1px solid #ECEDF3",display:"flex",flexDirection:"column",flexShrink:0,height:"100dvh",position:"sticky",top:0}}>
      <SidebarContent session={session} view={view} navigate={setView} condos={condos} onLogout={()=>{}}/>
    </div>
  );
}

/* ════════════════════════════════════
   TOPBAR
════════════════════════════════════ */
function TopBar({session,view,condos,tickets,onLogout}) {
  const cid=view.startsWith("condo-")?view.replace("condo-",""):view.startsWith("morador-")?view.replace("morador-",""):session.condoId;
  const condo=condos.find(c=>c.id===cid);
  const a=condo?getAcc(condo):"#EC4899";
  const openCount=condo?tickets.filter(t=>t.condoId===condo.id&&t.status==="open").length:tickets.filter(t=>t.status==="open").length;

  const labels = {
    "super":"Painel Central — Escuta Admin",
    "s-dash":`${condo?.name||""} — Dashboard`,
    "s-tickets":`${condo?.name||""} — Tickets`,
    "s-qr":`${condo?.name||""} — QR Code`,
  };
  const label = labels[view] || (view.startsWith("condo-")&&condo?`${condo.name} — Gestor`:view.startsWith("morador-")&&condo?`Portal Morador — ${condo.name}`:"");

  return (
    <div style={{background:"#F7F8FC",borderBottom:"1px solid #ECEDF3",padding:"0 20px",height:52,display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
      <div style={{fontSize:14,fontWeight:600,color:"#6B7280"}}>{label}</div>
      <div style={{display:"flex",alignItems:"center",gap:9}}>
        {openCount>0 && <span style={{background:"rgba(245,158,11,.12)",color:"#f59e0b",border:"1px solid rgba(245,158,11,.2)",borderRadius:20,padding:"3px 11px",fontSize:12,fontWeight:600}}><Bell size={12} style={{verticalAlign:"-1px",marginRight:3}}/>{openCount} aberto{openCount>1?"s":""}</span>}
        <div style={{display:"flex",alignItems:"center",gap:8,background:"#FFFFFF",border:"1px solid #ECEDF3",borderRadius:10,padding:"5px 12px"}}>
          <div style={{width:26,height:26,borderRadius:"50%",background:`linear-gradient(135deg,${a},${a}bb)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:"#000"}}>{session.name[0].toUpperCase()}</div>
          <div><div style={{fontSize:12,fontWeight:600,color:"#20264B",lineHeight:1}}>{session.name.split(" ")[0]}</div><div style={{fontSize:10,color:"#9CA3AF",marginTop:1}}>{session.role==="super"?"Super Admin":"Síndico"}</div></div>
          <button className="rbtn" onClick={onLogout} style={{padding:"4px 10px",fontSize:12,marginLeft:4}}>Sair</button>
        </div>
      </div>
    </div>
  );
}
/* ════════════════════════════════════
   APP ROOT
════════════════════════════════════ */
export default function EscutaOS() {
  const [session,    setSession]    = useState(null);
  const [condos,     setCondos]     = useState([]);
  const [tickets,    setTickets]    = useState([]);
  const [users,      setUsers]      = useState([]);
  const [view,       setView]       = useState("super");
  const [toasts,     setToasts]     = useState([]);
  const [sideOpen,   setSideOpen]   = useState(false);

  const addToast = useCallback((msg, type="info", duration=5000) => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, {id,msg,type}]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  }, []);

  // Carrega os condomínios do banco (API). id no frontend = token; dbId = UUID p/ API.
  const reloadCondos = useCallback(() => fetch("/api/condos")
    .then(r => r.json())
    .then(data => { if (Array.isArray(data)) setCondos(data.map(c => ({ ...c, id: c.token, dbId: c.id }))); })
    .catch(() => {}), []);
  useEffect(() => { reloadCondos(); }, [reloadCondos]);

  // Tickets do banco (lista com mensagens) + sincronização idempotente (por code TK-xxxx)
  const reloadTickets = useCallback(() => fetch("/api/tickets")
    .then(r => r.json())
    .then(data => { if (Array.isArray(data)) setTickets(data); })
    .catch(() => {}), []);
  useEffect(() => { reloadTickets(); }, [reloadTickets]);
  const syncTicket = useCallback((t) => {
    if (!t) return;
    fetch("/api/tickets/sync", { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: t.id, condoToken: t.condoId, category: t.cat, icon: t.icon,
        status: t.status, esc: t.esc, rating: t.rating, resident: t.resident, msgs: t.msgs }) }).catch(() => {});
  }, []);

  // Usuários do banco
  const reloadUsers = useCallback(() => fetch("/api/users")
    .then(r => r.json())
    .then(data => { if (Array.isArray(data)) setUsers(data); })
    .catch(() => {}), []);
  useEffect(() => { reloadUsers(); }, [reloadUsers]);

  const handleLogin  = (u) => { setSession(u); setView(u.role==="super"?"super":"s-dash"); };
  const handleLogout = () => { setSession(null); setView("super"); setSideOpen(false); };
  const navigate     = (v) => { setView(v); setSideOpen(false); };

  // ── Convite: /convite/:token abre a página de criar senha (sem login) ──
  const inviteMatch = typeof window !== "undefined" ? window.location.pathname.match(/^\/convite\/([^/]+)\/?$/) : null;
  if (inviteMatch) return (<><Styles/><InviteScreen token={decodeURIComponent(inviteMatch[1])}/></>);

  // ── Acesso público via QR Code: /c/:token abre o portal do morador (sem login) ──
  const publicMatch = typeof window !== "undefined"
    ? window.location.pathname.match(/^\/c\/([^/]+)\/?$/)
    : null;
  if (publicMatch) {
    const token = decodeURIComponent(publicMatch[1]);
    const pubCondo = condos.find(c => c.token === token);
    return (
      <>
        <Styles/>
        <ToastContainer toasts={toasts}/>
        {pubCondo ? (
          <div style={{minHeight:"100dvh",background:"#F7F8FC"}}>
            <MoradorPortal condo={pubCondo} tickets={tickets} setTickets={setTickets} addToast={addToast} syncTicket={syncTicket}/>
          </div>
        ) : (
          <div style={{minHeight:"100dvh",background:"#FFFFFF",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,textAlign:"center",fontFamily:"Plus Jakarta Sans,sans-serif",color:"#20264B"}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:18}}><LogoSVG width={200} bgColor="#FFFFFF"/></div>
            <div style={{fontSize:46,marginBottom:10}}>🔍</div>
            <h1 style={{fontSize:20,fontWeight:800,color:"#20264B",marginBottom:8}}>Condomínio não encontrado</h1>
            <p style={{fontSize:14,color:"#6B7280",maxWidth:360,lineHeight:1.6}}>O QR Code lido não corresponde a nenhum condomínio ativo. Confirme com a administração se a placa está atualizada.</p>
          </div>
        )}
      </>
    );
  }

  if (!session) return (
    <>
      <Styles/>
      <LoginScreen onLogin={handleLogin}/>
    </>
  );

  const cid   = view.startsWith("condo-")   ? view.replace("condo-","")   :
                view.startsWith("morador-") ? view.replace("morador-","") :
                view.startsWith("s-")       ? session.condoId              : null;
  const condo   = condos.find(c => c.id===cid);
  const isSuper = session.role === "super";
  const myCondo = condos.find(c=>c.id===session.condoId);
  const navCondo = (id, mode) => navigate(mode==="morador" ? `morador-${id}` : `condo-${id}`);
  const openCount = condo
    ? tickets.filter(t=>t.condoId===condo.id&&t.status==="open").length
    : tickets.filter(t=>t.status==="open").length;

  let content = null;
  if (view==="super") {
    content = <SuperAdmin condos={condos} setCondos={setCondos} users={users} setUsers={setUsers} tickets={tickets} setTickets={setTickets} onNavCondo={navCondo} reloadCondos={reloadCondos} reloadUsers={reloadUsers}/>;
  } else if (view.startsWith("condo-") && condo) {
    content = <CondoPanel condo={condo} tickets={tickets} setTickets={setTickets} syncTicket={syncTicket} onSimulate={()=>navigate(`morador-${condo.id}`)}/>;
  } else if (view.startsWith("morador-") && condo) {
    content = <MoradorPortal condo={condo} tickets={tickets} setTickets={setTickets} addToast={addToast} syncTicket={syncTicket}/>;
  } else if (view.startsWith("s-") && condo) {
    const tabMap = {"s-dash":"dash","s-tickets":"tickets","s-qr":"qr"};
    content = <CondoPanelSindico condo={condo} tickets={tickets} setTickets={setTickets} syncTicket={syncTicket} initialTab={tabMap[view]||"dash"} onSimulate={()=>navigate(`morador-${condo.id}`)}/>;
  }

  const accentColor = condo ? getAcc(condo) : "#EC4899";

  // Barra de navegação mobile por perfil
  const mobileNavItems = isSuper
    ? [["super",Shield,"Central"], ...condos.slice(0,3).map(c=>[`condo-${c.id}`,Building2,c.name.split(" ")[0]])]
    : [["s-dash",LayoutDashboard,"Dashboard"],["s-tickets",Ticket,"Tickets"],["s-qr",QrCode,"QR Code"]];

  // Label do topbar
  const topLabel =
    view==="super"           ? "Painel Central" :
    view==="s-dash"          ? `${myCondo?.name||""} – Dashboard` :
    view==="s-tickets"       ? `${myCondo?.name||""} – Tickets` :
    view==="s-qr"            ? `${myCondo?.name||""} – QR Code` :
    view.startsWith("condo-")&&condo  ? `${condo.name} – Gestor` :
    view.startsWith("morador-")&&condo? `Portal – ${condo.name}` : "";

  return (
    <div style={{display:"flex",height:"100dvh",background:"#F7F8FC",overflow:"hidden",fontFamily:"Plus Jakarta Sans,sans-serif",color:"#20264B",position:"relative"}}>
      <Styles/>
      <ToastContainer toasts={toasts}/>

      {/* Overlay mobile */}
      {sideOpen && <div onClick={()=>setSideOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.65)",zIndex:98}}/>}

      {/* SIDEBAR DESKTOP */}
      <div className="sidebar-desktop" style={{width:210,background:"#FFFFFF",borderRight:"1px solid #ECEDF3",display:"flex",flexDirection:"column",flexShrink:0,height:"100dvh",position:"sticky",top:0}}>
        <SidebarContent session={session} view={view} navigate={navigate} condos={condos} onLogout={handleLogout}/>
      </div>

      {/* SIDEBAR DRAWER MOBILE */}
      {sideOpen && (
        <div style={{position:"fixed",left:0,top:0,bottom:0,width:250,background:"#FFFFFF",borderRight:"1px solid #ECEDF3",zIndex:99,display:"flex",flexDirection:"column",animation:"slideIn .22s ease"}}>
          <SidebarContent session={session} view={view} navigate={navigate} condos={condos} onLogout={handleLogout} onClose={()=>setSideOpen(false)}/>
        </div>
      )}

      {/* MAIN */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>

        {/* TOPBAR */}
        <div style={{background:"#FFFFFF",borderBottom:"1px solid #ECEDF3",padding:"0 14px",height:52,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <button onClick={()=>setSideOpen(p=>!p)} className="mobile-nav" style={{background:"none",border:"none",color:"#6B7280",fontSize:24,cursor:"pointer",padding:0,lineHeight:1,flexShrink:0}}>☰</button>
          <div className="mobile-nav" style={{flex:1,maxWidth:130}}><LogoSVG width={130} bgColor="#FFFFFF"/></div>
          <div className="topbar-label" style={{fontSize:14,fontWeight:600,color:"#6B7280",flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{topLabel}</div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:"auto"}}>
            {openCount>0 && <span style={{background:"rgba(245,158,11,.15)",color:"#f59e0b",border:"1px solid rgba(245,158,11,.25)",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:600}}><Bell size={12} style={{verticalAlign:"-1px",marginRight:3}}/>{openCount}</span>}
            <div style={{display:"flex",alignItems:"center",gap:7,background:"#FFFFFF",border:"1px solid #ECEDF3",borderRadius:10,padding:"5px 10px"}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:`linear-gradient(135deg,${accentColor},${accentColor}bb)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#000",flexShrink:0}}>{session.name[0].toUpperCase()}</div>
              <div style={{lineHeight:1,display:"flex",flexDirection:"column"}}>
                <div style={{fontSize:12,fontWeight:600,color:"#20264B"}}>{session.name.split(" ")[0]}</div>
                <div style={{fontSize:9,color:"#9CA3AF",marginTop:1}}>{isSuper?"Super Admin":"Síndico"}</div>
              </div>
              <button className="rbtn" onClick={handleLogout} style={{padding:"3px 9px",fontSize:11}}>Sair</button>
            </div>
          </div>
        </div>

        {/* CONTEÚDO */}
        <div className="main-padding" style={{flex:1,overflowY:"auto",padding:20}}>
          {content}
        </div>

        {/* BOTTOMBAR MOBILE */}
        <div className="mobile-nav" style={{borderTop:"1px solid #ECEDF3",background:"#FFFFFF",flexShrink:0,display:"flex"}}>
          {mobileNavItems.map(([v,Ic,lb])=>(
            <button key={v} onClick={()=>navigate(v)} style={{flex:1,background:"none",border:"none",padding:"9px 4px 10px",display:"flex",flexDirection:"column",alignItems:"center",gap:2,cursor:"pointer",color:view===v?"#EC4899":"#6B7280",fontFamily:"Plus Jakarta Sans,sans-serif",transition:"color .15s",minWidth:0}}>
              <Ic size={22}/>
              <span style={{fontSize:10,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:56}}>{lb}</span>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}
/* Painel do síndico com tab controlada */
function CondoPanelSindico({condo,tickets,setTickets,syncTicket,initialTab,onSimulate}) {
  const [tab,setTab]=useState(initialTab||"dash");
  useEffect(()=>setTab(initialTab||"dash"),[initialTab]);
  const a=getAcc(condo);
  const mine=tickets.filter(t=>t.condoId===condo.id);
  const cnt={all:mine.length,open:mine.filter(t=>t.status==="open").length,"in-progress":mine.filter(t=>t.status==="in-progress").length,resolved:mine.filter(t=>t.status==="resolved").length};
  return (
    <div>
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:"1px solid #ECEDF3"}}>
        {[["dash",LayoutDashboard,"Dashboard"],["tickets",Ticket,"Tickets"],["qr",QrCode,"QR Code"]].map(([v,Ic,l])=>(
          <button key={v} className={`tab-btn ${tab===v?"on":""}`} onClick={()=>setTab(v)} style={{display:"inline-flex",alignItems:"center",gap:7}}><Ic size={16}/>{l}</button>
        ))}
      </div>
      {tab==="dash" && (
        <div className="fi">
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(155px,1fr))",gap:12,marginBottom:20}}>
            <StatCard icon={<Ticket size={22}/>} label="Total"      value={cnt.all}            color={a}/>
            <StatCard icon={<CircleDot size={22}/>} label="Abertos"    value={cnt.open}           color="#f59e0b" sub={cnt.open>0?"aguardando":""}/>
            <StatCard icon={<Clock size={22}/>} label="Em Atend."  value={cnt["in-progress"]} color="#38bdf8"/>
            <StatCard icon={<CheckCircle2 size={22}/>} label="Resolvidos" value={cnt.resolved}        color="#34d399"/>
          </div>
          <div className="card">
            <p style={{fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"1px",marginBottom:14}}>ATIVIDADE RECENTE</p>
            {mine.length===0 && <p style={{color:"#9CA3AF",fontSize:13}}>Nenhum ticket ainda.</p>}
            {mine.slice(0,8).map(t=>(
              <div key={t.id} className="row">
                <EIcon e={t.icon} size={19}/>
                <div style={{flex:1}}><div style={{fontSize:13,fontWeight:500,color:"#20264B"}}>{t.cat}</div><div style={{fontSize:11,color:"#9CA3AF"}}>{t.id} · {t.resident?.name||"—"} · {fmtF(t.createdAt)}</div></div>
                <StatusTag status={t.status}/>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab==="tickets" && <div className="fi"><TicketPanel tickets={tickets} setTickets={setTickets} syncTicket={syncTicket} condoId={condo.id} accent={a}/></div>}
      {tab==="qr"      && <div className="fi"><PlacaGenerator condo={condo}/><div style={{marginTop:20}}>{onSimulate && <button onClick={onSimulate} className="gbtn" style={{fontSize:13}}>📱 Simular Escaneamento</button>}</div></div>}
    </div>
  );
}
